import { supabase } from '@/src/supabaseClient';
import { UserProfile } from '@/types';
import { toast } from 'sonner';
import { gamificationService } from './gamificationService';
import { pointsService } from './pointsService';
import { MonetaryEngine } from '../../api/utils/monetaryEngine';
import { apiClient } from '../api/client';
import { runtimeMonitor } from '@shared/monitoring/RuntimeMonitor';
import { normalizeConfidence } from '@/src/shared/utils/confidenceUtils';
import { normalizeProductSource } from '@/src/shared/utils/sourceUtils';
import { getAuthHeaders } from './authHeaders';
import { chamarAcaoServidor } from './acoesServidor';

// Simple in-memory cache
const cache: { [key: string]: { data: any, timestamp: number } } = {};
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export const supabaseService = {
  // Helper to get YYYY-MM-DD in local time to avoid timezone shifts with toISOString()
  formatDateLocal(date: Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  },

  // Cache helper
  async getCached(key: string, fetcher: () => Promise<any>, ttl: number = DEFAULT_TTL) {
    const now = Date.now();
    if (cache[key] && (now - cache[key].timestamp < ttl)) {
      runtimeMonitor.incrementCacheHit();
      return cache[key].data;
    }
    runtimeMonitor.incrementCacheMiss();
    const data = await fetcher();
    cache[key] = { data, timestamp: now };
    return data;
  },

  invalidateCache(key?: string) {
    if (key) {
      delete cache[key];
    } else {
      // Clear all
      Object.keys(cache).forEach(k => delete cache[k]);
    }
  },

  clearCache(key?: string) {
    this.invalidateCache(key);
  },

  // User Profile
  async getUserProfile(userId: string) {
    return this.getCached(`user_profile_${userId}`, async () => {
      let profileData: any = null;
      let apiSuccess = false;

      // Fetch the secure unified profile from the v1 API first
      try {
        const apiResponse = await apiClient.get('/profile');
        if (apiResponse.data && apiResponse.data.success) {
          const apiData = apiResponse.data.data;
          console.log("[API Success] Profile loaded successfully via GET /api/v1/profile in getUserProfile:", apiData);
          
          profileData = {
            id: apiData.id,
            nome: apiData.nome,
            email: apiData.email,
            cpf: apiData.cpf,
            celular: apiData.celular,
            tipo_perfil: apiData.tipoPerfil,
            photo_url: apiData.fotoPerfil,
          };
          apiSuccess = true;
        }
      } catch (apiError: any) {
        console.warn("[API Error] Failed to fetch profile via GET /api/v1/profile in getUserProfile, falling back to Supabase direct query:", apiError.message || apiError);
      }

      // Fetch normal user profile from Supabase
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        if (apiSuccess && profileData) {
          console.log("[Fallback active] Supabase failed, but returning API-derived profile data");
          return {
            ...profileData,
            status: true
          };
        }
        throw error;
      }

      // Convert photo storage path to signed URL if not already resolved by API
      if (data.photo_url && !data.photo_url.startsWith('http')) {
        try {
          data.photo_url = await this.getSignedUrl(data.photo_url);
        } catch (e) {
          console.error("Error getting signed URL for user photo:", userId, e);
        }
      }

      // Merge API results over Supabase results if successful to ensure clean/unmasked fields are prioritized
      let resolvedProfile = data;
      if (apiSuccess && profileData) {
        resolvedProfile = {
          ...data,
          ...profileData
        };
      }

      // Automatically resolve partner, merchant, and store linkages in parallel via Promise.all (Item 6)
      try {
        const cleanCnpj = resolvedProfile.cnpj ? String(resolvedProfile.cnpj).replace(/\D/g, '') : null;
        const email = resolvedProfile.email || '';

        const [pMatchRes, pEmailMatchRes, storeMatchRes, colabMatchRes, reqMatchRes, compCheckRes] = await Promise.all([
          // 1. Check partner link by CNPJ if not present
          (!resolvedProfile.parceiro_id && cleanCnpj)
            ? supabase.from('parceiros').select('id, nome_parceiro').eq('cnpj_parceiro', cleanCnpj).limit(1).maybeSingle()
            : Promise.resolve({ data: null }),
          // 2. Check partner link by email if not present
          (!resolvedProfile.parceiro_id && email)
            ? supabase.from('parceiros').select('id, nome_parceiro').or(`email_gestor.eq.${email},email_parceiro.eq.${email}`).limit(1).maybeSingle()
            : Promise.resolve({ data: null }),
          // 3. Check if user is gestor of a store
          supabase.from('lojas').select('id, parceiro_id, loja_nome').eq('gestor_loja_id', userId).limit(1).maybeSingle(),
          // 4. Check if collaborator
          supabase.from('loja_colaboradores').select('loja_id').or(`usuario_id.eq.${userId},usuario_email.eq.${email}`).limit(1).maybeSingle(),
          // 5. Check approved or pending merchant request
          supabase.from('solicitacoes_lojistas').select('*').eq('usuario_id', userId).order('data_registro', { ascending: false }).limit(1).maybeSingle(),
          // 6. Check complementary profile & questionnaire completion
          supabase.from('usuario_perfil_complementar').select('*').eq('usuario_id', userId).maybeSingle()
        ]);

        const pMatch = pMatchRes.data;
        if (pMatch?.id) {
          resolvedProfile.parceiro_id = pMatch.id;
          resolvedProfile.nome_parceiro = pMatch.nome_parceiro;
        }

        const pEmailMatch = pEmailMatchRes.data;
        if (!resolvedProfile.parceiro_id && pEmailMatch?.id) {
          resolvedProfile.parceiro_id = pEmailMatch.id;
          resolvedProfile.nome_parceiro = pEmailMatch.nome_parceiro;
        }

        const storeMatch = storeMatchRes.data;
        if (storeMatch?.id) {
          resolvedProfile.loja_id = storeMatch.id;
          resolvedProfile.is_store_gestor = true;
          if (!resolvedProfile.parceiro_id && storeMatch.parceiro_id) {
            resolvedProfile.parceiro_id = storeMatch.parceiro_id;
          }
        }

        const colabMatch = colabMatchRes.data;
        if (colabMatch?.loja_id) {
          resolvedProfile.is_collaborator = true;
          if (!resolvedProfile.loja_id) {
            resolvedProfile.loja_id = colabMatch.loja_id;
          }
        }

        const reqMatch = reqMatchRes.data;
        if (reqMatch) {
          resolvedProfile.merchant_request = reqMatch;
          if (!resolvedProfile.parceiro_id && reqMatch.parceiro_id) {
            resolvedProfile.parceiro_id = reqMatch.parceiro_id;
          }
        }

        const compCheck = compCheckRes.data;
        if (compCheck?.id || resolvedProfile.perfil_completo || resolvedProfile.nivel_cadastro === 'VALIDADO') {
          resolvedProfile.perfil_completo = true;
          resolvedProfile.perfilCompleto = true;
          resolvedProfile.cadastro_completo = true;
          resolvedProfile.has_complementary_profile = true;
          if (compCheck) {
            resolvedProfile.complementar = compCheck;
          }
        }
      } catch (linkErr) {
        console.warn("Could not resolve additional profile linkages:", linkErr);
      }

      return resolvedProfile;
    }, 15000); // 15 seconds cache
  },

  async getComplementaryProfile(userId: string) {
    try {
      // 1. Try via v1 API
      const apiRes = await apiClient.get('/profile/complementary');
      if (apiRes.data?.data) {
        return apiRes.data.data;
      }
    } catch (e) {
      console.debug("API getComplementaryProfile fallback to Supabase direct query");
    }

    // 2. Direct Supabase query
    const { data, error } = await supabase
      .from('usuario_perfil_complementar')
      .select('*')
      .eq('usuario_id', userId)
      .maybeSingle();

    if (error) {
      console.warn("Error getting complementary profile from supabase:", error);
      return null;
    }
    return data;
  },

  async updateComplementaryProfile(userId: string, payload: any) {
    try {
      const apiRes = await apiClient.post('/profile/complementary', payload);
      if (apiRes.data?.data) {
        this.invalidateCache(`user_profile_${userId}`);
        return { data: apiRes.data.data, error: null };
      }
    } catch (e: any) {
      console.warn("API updateComplementaryProfile fallback to direct Supabase operation:", e.message);
    }

    // Direct fallback
    const { data: existing } = await supabase
      .from('usuario_perfil_complementar')
      .select('id')
      .eq('usuario_id', userId)
      .maybeSingle();

    let result: any;
    if (existing?.id) {
      result = await supabase
        .from('usuario_perfil_complementar')
        .update({ ...payload, data_atualizacao: new Date().toISOString() })
        .eq('usuario_id', userId)
        .select()
        .maybeSingle();
    } else {
      result = await supabase
        .from('usuario_perfil_complementar')
        .insert([{ ...payload, usuario_id: userId, data_registro: new Date().toISOString(), data_atualizacao: new Date().toISOString() }])
        .select()
        .maybeSingle();
    }

    this.invalidateCache(`user_profile_${userId}`);
    return result;
  },

  async updateUserAvatar(userId: string, file: File) {
    // 1. Upload new avatar
    const path = await this.uploadFile(userId, 'avatars', file);

    // 2. Update database
    const { error } = await supabase
      .from('usuarios')
      .update({ photo_url: path })
      .eq('id', userId);

    if (error) throw error;
    return path;
  },

  async deleteUserAvatar(userId: string) {
    // 1. Get current avatar path
    const { data: profile, error: fetchError } = await supabase
      .from('usuarios')
      .select('photo_url')
      .eq('id', userId)
      .single();
    
    if (fetchError) throw fetchError;

    // 2. Delete from storage if it exists
    if (profile?.photo_url && !profile.photo_url.startsWith('http')) {
      try {
        await this.deleteFile(profile.photo_url);
      } catch (e) {
        console.error("Error deleting avatar from storage:", e);
      }
    }

    // 3. Update database
    const { error: updateError } = await supabase
      .from('usuarios')
      .update({ photo_url: null })
      .eq('id', userId);
    
    if (updateError) throw updateError;
  },

  async updateUserProfile(userId: string, updates: any) {
    // Normalize data if present
    const normalizedUpdates = { ...updates };
    
    if (normalizedUpdates.cpf) {
      normalizedUpdates.cpf = normalizedUpdates.cpf.replace(/\D/g, '');
    }
    if (normalizedUpdates.celular) {
      normalizedUpdates.celular = normalizedUpdates.celular.replace(/\D/g, '');
    }
    if (normalizedUpdates.chave_pix) {
      // For PIX, we might want to keep e-mail as is, but remove formatting from CPF/Phone
      if (normalizedUpdates.tipo_chave_pix === 'CPF' || normalizedUpdates.tipo_chave_pix === 'Celular') {
        normalizedUpdates.chave_pix = normalizedUpdates.chave_pix.replace(/\D/g, '');
      }
    }

    const { data, error } = await supabase
      .from('usuarios')
      .update(normalizedUpdates)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    this.invalidateCache(`user_profile_${userId}`);
    return data;
  },

  async requestPhoneVerification(userId: string, phone: string) {
    try {
      const response = await fetch('/api/send-otp', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ userId, phone })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Erro ao enviar SMS');
      return data;
    } catch (error) {
      console.error('Error requesting phone verification:', error);
      throw error;
    }
  },

  async verifyPhoneCode(userId: string, phone: string, code: string) {
    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ userId, phone, code })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Código inválido');
      this.invalidateCache(`user_profile_${userId}`);
      return data;
    } catch (error) {
      console.error('Error verifying phone code:', error);
      throw error;
    }
  },

  async getPendingUsers() {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .in('tipo_perfil', [UserProfile.COLLECTOR, UserProfile.COMPANY])
      .eq('status_aprovacao', 'Pendente')
      .order('data_registro', { ascending: false });
    
    if (error) throw error;

    // Process signed URLs for photos
    const processed = await Promise.all(data.map(async (u: any) => {
      if (u.photo_url && !u.photo_url.startsWith('http')) {
        try {
          u.photo_url = await this.getSignedUrl(u.photo_url);
        } catch (e) {
          console.error("Error signing photo_url for pending user:", u.id, e);
        }
      }
      return u;
    }));

    return processed;
  },

  async approveUser(userId: string) {
    const { error } = await supabase
      .from('usuarios')
      .update({ 
        status_aprovacao: 'Aprovado',
        motivo_rejeicao: null,
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (error) throw error;
    
    // Invalidate profile cache
    this.invalidateCache(`user_profile_${userId}`);
  },

  async rejectUser(userId: string, reason: string) {
    const { error } = await supabase
      .from('usuarios')
      .update({ 
        status_aprovacao: 'Rejeitado',
        motivo_rejeicao: reason,
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (error) throw error;

    // Invalidate profile cache
    this.invalidateCache(`user_profile_${userId}`);
  },

  // Collections (Coletas) - Now using descartes table
  async getCollections(userId: string) {
    const { data, error } = await supabase
      .from('descartes')
      .select('*, descartes_materiais(*)')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    
    // Map to a consistent format if needed, but for now just return the data
    return data;
  },

  async addCollection(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('addCollection', args);
  },

  async getTargetEmails(audience: string): Promise<string[]> {
    let query = supabase.from('usuarios').select('email').not('email', 'is', null);
    
    if (audience !== 'Todos' && audience !== 'Todos os Inscritos') {
      const dbProfile = audience === 'Usuários' ? 'Usuário' : audience === 'Catadores' ? 'Catador' : 'Empresa';
      query = query.eq('tipo_perfil', dbProfile);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    return data?.map(u => u.email).filter(email => email && email.includes('@')) || [];
  },

  async logEmailDispatch(dispatchData: { subject: string, body: string, audience: string, recipient_count: number, status: string }) {
    const { data, error } = await supabase
      .from('email_logs')
      .insert([{
        assunto: dispatchData.subject,
        corpo: dispatchData.body,
        publico_alvo: dispatchData.audience,
        quantidade_destinatarios: dispatchData.recipient_count,
        status: dispatchData.status,
        data_registro: new Date().toISOString()
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getEmailLogs() {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async getEmailTemplates() {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*');
    
    if (error && error.code !== 'PGRST116') { // Ignore "not found" errors for new tables
      console.error("Templates table might not exist yet:", error);
    }
    return data || [];
  },

  async getTargetPhones(audience: string) {
    let query = supabase.from('usuarios').select('celular').not('celular', 'is', null);
    
    if (audience === 'Usuários') query = query.eq('tipo_perfil', 'Usuário');
    if (audience === 'Catadores') query = query.eq('tipo_perfil', 'Catador');
    if (audience === 'Empresas') query = query.eq('tipo_perfil', 'Empresa');

    const { data, error } = await query;
    if (error) throw error;
    return data.map((n: any) => n.celular).filter((p: string) => p && p.length > 5);
  },

  async logSmsDispatch(log: { message: string, audience: string, recipient_count: number, status: string }) {
    const { data, error } = await supabase
      .from('sms_logs')
      .insert([{
        mensagem: log.message,
        publico_alvo: log.audience,
        total_destinatarios: log.recipient_count,
        status: log.status,
        data_registro: new Date().toISOString()
      }]);
    
    if (error) throw error;
    return data;
  },

  async getSmsLogs() {
    const { data, error } = await supabase
      .from('sms_logs')
      .select('*')
      .order('data_registro', { ascending: false });
    
    if (error && error.code !== 'PGRST116') {
      console.error("SMS logs table might not exist:", error);
    }
    return data || [];
  },

  // Collectors and Materials
  async getCollectors() {
    return this.getCached('collectors', async () => {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, photo_url, celular, latitude, longitude, endereco')
        .eq('tipo_perfil', 'Catador');
      
      if (error) throw error;

      // Process photos
      const processed = await Promise.all(data.map(async (u: any) => {
        if (u.photo_url && !u.photo_url.startsWith('http')) {
          try {
            u.photo_url = await this.getSignedUrl(u.photo_url);
          } catch (e) {
            console.error("Error getting signed URL for collector:", u.id, e);
          }
        }
        return u;
      }));

      return processed;
    }, 300000); // 5 minutes for collectors
  },

  async getCollectorMaterials(collectorId: string) {
    const { data, error } = await supabase
      .from('materiais_catadores')
      .select(`
        material_id,
        materiais (*)
      `)
      .eq('usuario_id', collectorId);
    
    if (error) throw error;
    return data.map(item => item.materiais);
  },

  async getAllMaterials() {
    const { data, error } = await supabase
      .from('materiais')
      .select('*')
      .order('material', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  async sendSms(number: string, message: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ number, message })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in sendSms service:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error during SMS sending' 
      };
    }
  },

  async saveEmailTemplate(template: { id: string, name: string, subject: string, body: string }) {
    const { error } = await supabase
      .from('email_templates')
      .upsert([{
        id: template.id,
        nome: template.name,
        assunto: template.subject,
        corpo: template.body,
        data_atualizacao: new Date().toISOString()
      }]);
    
    if (error) throw error;
  },

  // WasteIn (Social Feed)
  async getWasteInPosts(currentUserId?: string) {
    const { data, error } = await supabase
      .from('wastein_posts')
      .select(`
        *,
        usuarios!wastein_posts_usuario_id_fkey(
          nome, 
          photo_url, 
          tipo_perfil,
          exibir_liga,
          usuarios_ligas (
            ligas (
              nome
            )
          )
        ),
        wastein_post_midias(url, tipo),
        wastein_post_curtidas(usuario_id),
        wastein_post_comentarios(id),
        wastein_post_denuncias(usuario_id)
      `)
      .not('status', 'in', '("INATIVO","REMOVIDO")')
      .order('status', { ascending: false })
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    return this.processPosts(data, currentUserId);
  },

  async getComments(postId: string) {
    const { data, error } = await supabase
      .from('wastein_post_comentarios')
      .select(`
        *,
        usuarios(
          nome, 
          photo_url,
          exibir_liga,
          usuarios_ligas (
            ligas (
              nome
            )
          )
        )
      `)
      .eq('post_id', postId)
      .order('data_registro', { ascending: true });
    
    if (error) throw error;
    
    // Process comments to include signed URLs for photos
    const processedComments = await Promise.all(data.map(async (comment: any) => {
      const photoPath = comment.usuarios?.photo_url;
      if (photoPath && !photoPath.startsWith('http')) {
        try {
          if (comment.usuarios) {
            comment.usuarios.photo_url = await this.getSignedUrl(photoPath);
          }
        } catch (e) {
          console.error("Error getting signed URL for comment photo:", comment.usuario_id, e);
        }
      }
      return comment;
    }));

    return processedComments;
  },

  async getManagementPosts() {
    const { data, error } = await supabase
      .from('wastein_posts')
      .select('*')
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    return this.processPosts(data);
  },

  async getPostMedia(postId: string) {
    const { data, error } = await supabase
      .from('wastein_post_midias')
      .select('*')
      .eq('post_id', postId);
    
    if (error) throw error;

    // Process URLs
    const processedMedia = await Promise.all((data || []).map(async (m: any) => {
      if (m.url && !m.url.startsWith('https://ui-avatars.com')) {
        try {
          m.url = await this.getSignedUrl(m.url);
        } catch (e) {
          console.error("Error getting signed URL for post media:", m.url, e);
        }
      }
      return m;
    }));

    return processedMedia;
  },

  async processPosts(data: any[], currentUserId?: string) {
    const processedPosts = await Promise.all(data.map(async (post: any) => {
      // Use actual counts from related tables
      post.likesCount = post.wastein_post_curtidas?.length || 0;
      post.commentsCount = post.wastein_post_comentarios?.length || 0;
      post.isLiked = post.wastein_post_curtidas?.some((like: any) => like.usuario_id === currentUserId);
      post.isReported = post.wastein_post_denuncias?.some((report: any) => report.usuario_id === currentUserId && !report.resolvido);
      
      post.reportsCount = post.total_denuncias || 0;
      post.userName = post.nome_user || post.usuarios?.nome || 'Usuário';
      post.photo_url = post.usuarios?.photo_url || post.foto_user;
      post.exibir_liga = post.usuarios?.exibir_liga;
      post.liga_nome = post.usuarios?.usuarios_ligas?.[0]?.ligas?.nome;
      post.userId = post.usuario_id;
      post.content = post.conteudo;
      post.status = post.status || 'ATIVO';

      // Check if highlight has expired
      if (post.status === 'DESTACADO' && post.destaque_expira_em) {
        const expirationDate = new Date(post.destaque_expira_em);
        if (expirationDate < new Date()) {
          post.status = 'ATIVO';
          // Update database asynchronously
          supabase
            .from('wastein_posts')
            .update({ status: 'ATIVO', destaque_expira_em: null })
            .eq('id', post.id)
            .then(({ error }) => {
              if (error) console.error("Error updating expired post status:", error);
            });
        }
      }

      // 1. Signed URLs for user photo
      const isSupabaseUrl = typeof post.photo_url === 'string' && post.photo_url.includes('.supabase.co/storage/v1/object/');
      if (post.photo_url && (!post.photo_url.startsWith('http') || isSupabaseUrl)) {
        try {
          post.photo_url = await this.getSignedUrl(post.photo_url);
        } catch (e) {
          console.error("Error getting signed URL for post user photo:", post.photo_url, e);
        }
      }

      // 2. Signed URLs for post media
      if (post.wastein_post_midias && post.wastein_post_midias.length > 0) {
        post.wastein_post_midias = await Promise.all(post.wastein_post_midias.map(async (m: any) => {
          if (m.url && !m.url.startsWith('http')) {
            try {
              m.url = await this.getSignedUrl(m.url);
            } catch (e) {
              console.error("Error getting signed URL for media:", m.url, e);
            }
          }
          return m;
        }));
      }

      return post;
    }));

    return processedPosts;
  },

  async updatePostStatus(postId: string, status: string) {
    const updateData: any = { 
      status: status.toUpperCase()
    };

    // If highlighting, set the highlight expiration to 24 hours from now
    if (status.toUpperCase() === 'DESTACADO') {
      const expirationDate = new Date();
      expirationDate.setHours(expirationDate.getHours() + 24);
      updateData.destaque_expira_em = expirationDate.toISOString();
    }

    const { error } = await supabase
      .from('wastein_posts')
      .update({ 
        ...updateData,
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', postId);
    
    if (error) throw error;
  },

  async dismissReports(postId: string) {
    // 1. Update all reports to resolvido = true
    const { error: reportError } = await supabase
      .from('wastein_post_denuncias')
      .update({ 
        resolvido: true 
      })
      .eq('post_id', postId)
      .eq('resolvido', false);
    
    if (reportError) throw reportError;

    // 2. Reset total_denuncias and status on the post
    const { error: postError } = await supabase
      .from('wastein_posts')
      .update({ 
        total_denuncias: 0,
        status: 'ATIVO',
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', postId);
    
    if (postError) throw postError;
  },

  async createWasteInPost(userId: string, conteudo: string, nome_user: string, foto_user: string, mediaFiles?: File[]) {
    // 1. Create the post
    const { data: post, error: postError } = await supabase
      .from('wastein_posts')
      .insert([{ 
        usuario_id: userId, 
        conteudo,
        nome_user,
        foto_user
      }])
      .select()
      .single();
    
    if (postError) throw postError;

    // 2. Upload and link media if provided
    if (mediaFiles && mediaFiles.length > 0) {
      const mediaPromises = mediaFiles.map(async (file) => {
        const path = await this.uploadFile(userId, 'wastein_posts', file);
        const tipo = file.type.startsWith('video') ? 'video' : 'imagem';
        
        const { error: mediaError } = await supabase
          .from('wastein_post_midias')
          .insert([{ post_id: post.id, url: path, tipo }]);
        
        if (mediaError) throw mediaError;
      });

      await Promise.all(mediaPromises);
    }

    return post;
  },

  async toggleLikePost(postId: string, userId: string) {
    // Check if already liked
    const { data: existingLike } = await supabase
      .from('wastein_post_curtidas')
      .select('*')
      .eq('post_id', postId)
      .eq('usuario_id', userId)
      .maybeSingle();

    if (existingLike) {
      // Unlike
      const { error } = await supabase
        .from('wastein_post_curtidas')
        .delete()
        .eq('post_id', postId)
        .eq('usuario_id', userId);
      if (error) throw error;

      // Decrement counter manually
      const { data: post } = await supabase.from('wastein_posts').select('total_curtidas').eq('id', postId).single();
      await supabase.from('wastein_posts').update({ total_curtidas: Math.max(0, (post?.total_curtidas || 0) - 1) }).eq('id', postId);

      return { liked: false };
    } else {
      // Like
      const { error } = await supabase
        .from('wastein_post_curtidas')
        .insert([{ post_id: postId, usuario_id: userId }]);
      if (error) throw error;

      // Increment counter manually
      const { data: post } = await supabase.from('wastein_posts').select('total_curtidas').eq('id', postId).single();
      await supabase.from('wastein_posts').update({ total_curtidas: (post?.total_curtidas || 0) + 1 }).eq('id', postId);

      return { liked: true };
    }
  },

  async addComment(postId: string, userId: string, conteudo: string) {
    const { data, error } = await supabase
      .from('wastein_post_comentarios')
      .insert([{ post_id: postId, usuario_id: userId, conteudo }])
      .select('*, usuarios(nome, photo_url)')
      .single();
    
    if (error) throw error;

    // Increment counter manually
    const { data: post } = await supabase.from('wastein_posts').select('total_comentarios').eq('id', postId).single();
    await supabase.from('wastein_posts').update({ total_comentarios: (post?.total_comentarios || 0) + 1 }).eq('id', postId);

    // Process signed URL for user photo
    if (data.usuarios?.photo_url && !data.usuarios.photo_url.startsWith('http')) {
      try {
        data.usuarios.photo_url = await this.getSignedUrl(data.usuarios.photo_url);
      } catch (e) {
        console.error("Error getting signed URL for comment user photo:", data.usuarios.photo_url, e);
      }
    }

    return data;
  },

  async reportPost(postId: string, userId: string, motivo: string, descricao?: string) {
    // Check if already reported by this user
    const { data: existingReport } = await supabase
      .from('wastein_post_denuncias')
      .select('id')
      .eq('post_id', postId)
      .eq('usuario_id', userId)
      .maybeSingle();

    if (existingReport) {
      throw new Error('Você já denunciou este post.');
    }

    const { error } = await supabase
      .from('wastein_post_denuncias')
      .insert([{ 
        post_id: postId, 
        usuario_id: userId, 
        motivo, 
        descricao,
        resolvido: false
      }]);
    
    if (error) throw error;

    // Increment counter manually and update post status to DENUNCIADO
    const { data: post } = await supabase.from('wastein_posts').select('total_denuncias').eq('id', postId).single();
    await supabase.from('wastein_posts').update({ 
      total_denuncias: (post?.total_denuncias || 0) + 1,
      status: 'DENUNCIADO'
    }).eq('id', postId);
  },

  async softDeleteWasteInPost(postId: string, userId?: string) {
    // 1. Update post status to REMOVIDO
    let query = supabase
      .from('wastein_posts')
      .update({ 
        status: 'REMOVIDO',
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', postId);
    
    if (userId) {
      query = query.eq('usuario_id', userId);
    }

    const { error } = await query;
    if (error) throw error;

    // 2. Resolve all reports as RESOLVIDO
    await supabase
      .from('wastein_post_denuncias')
      .update({ 
        resolvido: true 
      })
      .eq('post_id', postId)
      .eq('resolvido', false);
  },

  async hardDeleteWasteInPost(postId: string) {
    // Delete record permanently from the database
    const { error } = await supabase
      .from('wastein_posts')
      .delete()
      .eq('id', postId);
      
    if (error) throw error;
  },

  // Notifications
  async getNotifications(userId: string) {
    const { data, error } = await supabase
      .from('notificacoes')
      .select('*')
      .eq('usuario_id', userId)
      .eq('exibe', true)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async markNotificationAsRead(notificationId: string) {
    const { error } = await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('id', notificationId);
    
    if (error) throw error;
  },

  async clearNotifications(userId: string) {
    const { error } = await supabase
      .from('notificacoes')
      .update({ exibe: false })
      .eq('usuario_id', userId);
    
    if (error) throw error;
  },

  // Check-ins
  async getTodayCheckIn(userId: string) {
    const today = this.formatDateLocal(new Date());
    const { data, error } = await supabase
      .from('checkins')
      .select('*')
      .eq('usuario_id', userId)
      .eq('data_checkin', today)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  },

  async getWeeklyCheckIns(userId: string) {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (Sun) to 6 (Sat)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    const startStr = this.formatDateLocal(startOfWeek);

    const { data, error } = await supabase
      .from('checkins')
      .select('data_checkin')
      .eq('usuario_id', userId)
      .gte('data_checkin', startStr)
      .order('data_checkin', { ascending: true });

    if (error) throw error;
    return data;
  },

  async performCheckIn(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('performCheckIn', args);
  },

  async getCashbackRedemptionSummary(userId: string): Promise<{
    totalPontos: number;
    totalReais: number;
    resumoPorOrigem: { origem: string; pontos: number; valorReais: number }[];
  }> {
    const { data: creditsData, error } = await supabase
      .from('usuario_pontos')
      .select('origem, saldo_restante, valor_unitario')
      .eq('usuario_id', userId)
      .eq('tipo_ponto', 'CREDITO')
      .eq('status', 'CONSOLIDADO')
      .gt('saldo_restante', 0);

    if (error) throw error;

    let totalPontos = 0;
    let totalReais = 0;
    const resumoMap = new Map<string, { pontos: number; valorReais: number }>();

    for (const row of (creditsData || [])) {
      const pontos = Number(row.saldo_restante || 0);
      const valor = Number((pontos * Number(row.valor_unitario || 0)).toFixed(4));
      totalPontos += pontos;
      totalReais = Number((totalReais + valor).toFixed(4));

      const origemKey = String(row.origem || 'DESCONHECIDA').toUpperCase();
      const acc = resumoMap.get(origemKey) || { pontos: 0, valorReais: 0 };
      acc.pontos += pontos;
      acc.valorReais = Number((acc.valorReais + valor).toFixed(4));
      resumoMap.set(origemKey, acc);
    }

    return {
      totalPontos,
      totalReais,
      resumoPorOrigem: Array.from(resumoMap.entries()).map(([origem, v]) => ({ origem, pontos: v.pontos, valorReais: v.valorReais }))
    };
  },

  /**
   * Consome, em ordem FIFO, as entradas de pontos do usuário com valor rastreado,
   * registrando o débito para auditoria. Espelha `FIFOEngine.runFIFORedemption` do
   * backend (ver nota de arquitetura acima) — mudanças num lugar devem ser replicadas
   * no outro até a lógica ser unificada atrás de um endpoint autenticado.
   */
  async consumeFIFOCashback(userId: string, pontosSolicitados: number, referenciaResgateId?: string | null): Promise<{
    cashbackTotalReais: number;
    resumoPorOrigem: { origem: string; pontos: number; valorReais: number }[];
  }> {
    const { data: creditsData, error } = await supabase
      .from('usuario_pontos')
      .select('*')
      .eq('usuario_id', userId)
      .eq('tipo_ponto', 'CREDITO')
      .eq('status', 'CONSOLIDADO')
      .gt('saldo_restante', 0)
      .order('created_at', { ascending: true });

    if (error) throw error;

    const fatiasDisponiveis = (creditsData || []).filter(c => Number(c.saldo_restante || 0) > 0);

    let pontosRestantes = pontosSolicitados;
    let cashbackTotalReais = 0;
    const resumoMap = new Map<string, { pontos: number; valorReais: number }>();
    const atualizacoes: { id: string; novoSaldo: number }[] = [];

    for (const fatia of fatiasDisponiveis) {
      if (pontosRestantes <= 0) break;
      const disponivel = Number(fatia.saldo_restante || 0);
      const consumir = Math.min(disponivel, pontosRestantes);
      if (consumir <= 0) continue;

      const valorUnitario = Number(fatia.valor_unitario || 0);
      const valor = Number((consumir * valorUnitario).toFixed(4));
      cashbackTotalReais = Number((cashbackTotalReais + valor).toFixed(4));
      pontosRestantes -= consumir;
      atualizacoes.push({ id: fatia.id, novoSaldo: disponivel - consumir });

      const origemKey = String(fatia.origem || 'DESCONHECIDA').toUpperCase();
      const acc = resumoMap.get(origemKey) || { pontos: 0, valorReais: 0 };
      acc.pontos += consumir;
      acc.valorReais = Number((acc.valorReais + valor).toFixed(4));
      resumoMap.set(origemKey, acc);
    }

    if (pontosRestantes > 0) {
      throw new Error('Saldo de pontos com valor rastreado insuficiente para este resgate.');
    }

    // Persiste o consumo de cada fatia
    for (const upd of atualizacoes) {
      const { error: updErr } = await supabase
        .from('usuario_pontos')
        .update({ saldo_restante: upd.novoSaldo, updated_at: new Date().toISOString() })
        .eq('id', upd.id);
      if (updErr) throw updErr;
    }

    // Registra o débito consolidado para auditoria
    await supabase.from('usuario_pontos').insert([{
      usuario_id: userId,
      origem: 'RESGATE_CASHBACK',
      tipo_ponto: 'DEBITO',
      quantidade_pontos: pontosSolicitados,
      peso_financeiro: 0,
      valor_unitario: pontosSolicitados > 0 ? Number((cashbackTotalReais / pontosSolicitados).toFixed(4)) : 0,
      valor_total: cashbackTotalReais,
      saldo_restante: 0,
      status: 'CONSOLIDADO',
      referencia_id: referenciaResgateId || null
    }]);

    return {
      cashbackTotalReais,
      resumoPorOrigem: Array.from(resumoMap.entries()).map(([origem, v]) => ({ origem, pontos: v.pontos, valorReais: v.valorReais }))
    };
  },

  async getExchanges(userId: string) {
    const { data, error } = await supabase
      .from('trocas')
      .select('*')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async getStatement(userId: string) {
    // NOTA: a rota /api/v1/statement/usuario/:userId lê somente da tabela `usuario_pontos`, que hoje
    // só é populada pelo fluxo de descarte via máquina/RVM. Usá-la como atalho de "sucesso" quebrava
    // o Extrato de duas formas: (1) um array vazio (`json.data = []`, um valor truthy em JS) era
    // tratado como sucesso e o código abaixo nunca rodava — escondendo todo o histórico de check-in/
    // missão/quiz/resgate que só existe em `extrato`; (2) mesmo quando não-vazio, retornava só as
    // entradas de `usuario_pontos`, ocultando as mesmas movimentações legadas. A lógica abaixo
    // (extrato + reconciliação direta com `descartes`, a fonte real de qualquer descarte — seja da
    // máquina ou do app) já é estritamente mais completa, então ela é usada sempre, como fonte única.
    const { data: extratoData, error } = await supabase
      .from('extrato')
      .select('*, parceiros(nome_parceiro), resgates(observacao, status)')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;

    // Also fetch direct discards to guarantee nothing is missing
    const { data: discardsData } = await supabase
      .from('descartes')
      .select('id, ecoponto_id, parceiro_id, pontos, qtd_peso_gramas, data_registro, descartes_materiais(*), ecopontos(nome), parceiros(nome_parceiro)')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });

    const existingDiscardIds = new Set((extratoData || []).filter(e => e.descarte_id).map(e => e.descarte_id));
    const items = [...(extratoData || [])];

    if (discardsData) {
      for (const d of discardsData) {
        if (!existingDiscardIds.has(d.id)) {
          const matName = d.descartes_materiais?.[0]?.material || 'Resíduo';
          const weightKg = (Number(d.qtd_peso_gramas) || 0) / 1000;
          items.push({
            id: `disc_${d.id}`,
            usuario_id: userId,
            descarte_id: d.id,
            descricao: `Descarte de ${weightKg.toFixed(2)}kg de ${matName}`,
            entrada: true,
            pontos: d.pontos || 0,
            data_registro: d.data_registro || new Date().toISOString(),
            parceiros: d.parceiros
          });
        }
      }
    }

    // Sort descending by date
    return items.sort((a, b) => new Date(b.data_registro).getTime() - new Date(a.data_registro).getTime());
  },

  async getActiveAds(userId?: string) {
    const { data, error } = await supabase
      .from('anuncio_publicidade')
      .select('*, anuncio_publicidade_parceiros(parceiro_id)')
      .eq('status_ativo', true)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;

    let filteredAds = data;

    if (userId) {
      // 1. Get partners user has discarded with
      const { data: userDiscards } = await supabase
        .from('descartes')
        .select('parceiro_id')
        .eq('usuario_id', userId)
        .not('parceiro_id', 'is', null);
      
      const userPartnerIds = new Set(userDiscards?.map(d => d.parceiro_id) || []);

      // 2. Filter ads
      filteredAds = data.filter((ad: any) => {
        const adPartnerIds = ad.anuncio_publicidade_parceiros?.map((p: any) => p.parceiro_id) || [];
        
        // If ad has no partners, it's for everyone
        if (adPartnerIds.length === 0) return true;

        // If ad has partners, user must have discarded with at least one of them
        return adPartnerIds.some((pId: string) => userPartnerIds.has(pId));
      });
    }

    // Process banner URLs
    const processedAds = await Promise.all(filteredAds.map(async (ad: any) => {
      if (ad.banner && !ad.banner.startsWith('http')) {
        try {
          ad.banner = await this.getSignedUrl(ad.banner);
        } catch (e) {
          console.error("Error getting signed URL for ad banner:", ad.banner, e);
        }
      }
      return ad;
    }));

    return processedAds;
  },

  /**
   * Atomically adds points to a user using Optimistic Concurrency Control (OCC).
   * Prevents lost updates under concurrent transactions.
   */
  async atomicAddUserPoints(userId: string, pointsToAdd: number, maxRetries = 5): Promise<number> {
    return pointsService.atomicAddUserPoints(userId, pointsToAdd, maxRetries);
  },

  /**
   * Atomically deducts points from a user using Optimistic Concurrency Control (OCC).
   * Prevents double-spending under concurrent redemptions.
   */
  async atomicDeductUserPoints(userId: string, pointsToDeduct: number, maxRetries = 5): Promise<number> {
    return pointsService.atomicDeductUserPoints(userId, pointsToDeduct, maxRetries);
  },

  /**
   * Atomically decrements reward inventory using Optimistic Concurrency Control (OCC).
   * Prevents overselling rewards under concurrent requests.
   */
  async atomicDecrementRewardStock(recompensaId: string, maxRetries = 5): Promise<number> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const { data: reward, error } = await supabase
        .from('recompensas')
        .select('quantidade')
        .eq('id', recompensaId)
        .single();

      if (error || !reward) throw new Error('Recompensa não encontrada');
      if (Number(reward.quantidade || 0) <= 0) {
        throw new Error('Esta recompensa está esgotada.');
      }

      const currentQty = Number(reward.quantidade);
      const { data: updated, error: updateError } = await supabase
        .from('recompensas')
        .update({ 
          quantidade: currentQty - 1, 
          data_atualizacao: new Date().toISOString() 
        })
        .eq('id', recompensaId)
        .eq('quantidade', currentQty) // OCC guard
        .gt('quantidade', 0)
        .select('id, quantidade')
        .maybeSingle();

      if (!updateError && updated) {
        return updated.quantidade;
      }
      await new Promise(res => setTimeout(res, 20 + Math.random() * 50));
    }
    throw new Error('Falha de concorrência ao reservar estoque da recompensa.');
  },

  async performExchange(userId: string, item: { nome: string, pontos_custo: number, codigo_cupom: string }) {
    // 1. Atomically deduct points first with OCC guard
    await this.atomicDeductUserPoints(userId, item.pontos_custo);

    // 2. Record exchange and extrato
    try {
      const { data, error } = await supabase
        .from('resgates')
        .insert([{ ...item, usuario_id: userId }])
        .select()
        .single();
      
      if (error) throw error;

      await supabase
        .from('extrato')
        .insert([{
          usuario_id: userId,
          descricao: `Resgate de ${item.nome}`,
          entrada: false,
          pontos: item.pontos_custo,
          data_registro: new Date().toISOString()
        }]);

      return data;
    } catch (err) {
      // Rollback points if recording exchange fails
      try {
        await this.atomicAddUserPoints(userId, item.pontos_custo);
      } catch (rollbackErr) {
        console.error('Falha crítica ao reverter pontos de resgate:', rollbackErr);
      }
      throw err;
    }
  },

  // Storage Helpers
  async ensureBucketExists(bucketName: string) {
    try {
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      if (listError) throw listError;

      const exists = buckets.some(b => b.name === bucketName);
      if (!exists) {
        const { error: createError } = await supabase.storage.createBucket(bucketName, {
          public: true,
          allowedMimeTypes: ['image/*', 'video/*'],
          fileSizeLimit: 52428800 // 50MB
        });
        if (createError) {
          // If it's a permission error, we might not be able to create it, but we tried
          console.warn(`Could not create bucket ${bucketName}:`, createError.message);
        }
      }
    } catch (err) {
      console.warn(`Error checking/creating bucket ${bucketName}:`, err);
    }
  },

  async uploadFile(userId: string, feature: string, file: File, bucket: string = 'app-files') {
    const extension = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}.${extension}`;
    const path = `${userId}/${feature}/${fileName}`;

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);

    if (error) throw error;
    // Return path including bucket for resolution
    return `${bucket}/${path}`;
  },

  sanitizeStorageUrl(url?: string | null): string {
    if (!url || typeof url !== 'string' || url === 'null' || url === 'undefined' || url.trim() === '') {
      return '';
    }
    const trimmed = url.trim();
    if (trimmed.includes('/storage/v1/object/sign/')) {
      return trimmed.replace('/storage/v1/object/sign/', '/storage/v1/object/public/').split('?')[0];
    }
    return trimmed;
  },

  async getSignedUrl(path: string) {
    if (!path || typeof path !== 'string' || path === 'null' || path === 'undefined' || path.trim() === '') {
      return '';
    }
    
    let actualPath = path.trim();
    let bucketName = 'app-files';

    // Fast sanitize: If it's a signed storage URL, convert to clean public URL directly
    if (actualPath.includes('/storage/v1/object/sign/')) {
      actualPath = this.sanitizeStorageUrl(actualPath);
    }

    // Handle full Supabase storage URLs
    if (actualPath.startsWith('http')) {
      if (actualPath.includes('.supabase.co/storage/v1/object/')) {
        // Extract bucket and path from URL
        const match = actualPath.match(/\/object\/(?:sign|public|authenticated)\/([^\/]+)\/(.+)$/);
        if (match) {
          bucketName = match[1];
          actualPath = match[2].split('?')[0]; // Remove query params
          try {
            actualPath = decodeURIComponent(actualPath);
          } catch (e) {
            console.warn("Could not decode path:", actualPath);
          }
        } else {
          return actualPath.split('?')[0]; // Fallback to URL without token
        }
      } else {
        return actualPath; // External URL
      }
    }
    
    // Normalize path by removing leading/trailing slashes
    while (actualPath.startsWith('/')) {
      actualPath = actualPath.substring(1);
    }
    while (actualPath.endsWith('/')) {
      actualPath = actualPath.substring(0, actualPath.length - 1);
    }

    // If the path starts with a known bucket name (using path matching)
    const publicBuckets = ['app-files', 'avatars', 'public', 'public_assets', 'coletas'];
    for (const b of publicBuckets) {
      if (actualPath.startsWith(b + '/')) {
        bucketName = b;
        actualPath = actualPath.substring(b.length + 1);
        break;
      }
    }

    // Clean up actualPath to ensure no duplicate slashes
    actualPath = actualPath.replace(/\/+/g, '/');
    if (actualPath.startsWith('/')) {
      actualPath = actualPath.substring(1);
    }

    // Get public URL
    const { data: publicData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(actualPath);

    // For public buckets, always return permanent public URL (never expires, avoids InvalidJWT error)
    if (publicBuckets.includes(bucketName)) {
      return publicData.publicUrl;
    }
    
    // Try to get a signed URL if we're authenticated (only for private buckets)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: signedData, error: signedError } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(actualPath, 604800); // 1 week
          
        if (!signedError && signedData?.signedUrl) {
          return signedData.signedUrl;
        }
      }
    } catch (e) {
      // Ignore and fallback to public URL
    }

    return publicData.publicUrl;
  },

  async deleteFile(path: string) {
    const { error } = await supabase.storage
      .from('app-files')
      .remove([path]);

    if (error) throw error;
  },

  // Ranking
  async getRankings(tipo: 'MENSAL' | 'ANUAL' | 'TOTAL' | 'GLOBAL') {
    let query = supabase
      .from('ranking')
      .select('*')
      .eq('tipo', tipo)
      .order('data_registro', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  },

  async getRankingUsers(rankingId: string, limit = 10, offset = 0, sortBy: 'volume' | 'descartes' = 'volume') {
    const { data, error } = await supabase
      .from('ranking_usuarios')
      .select(`
        *,
        usuarios (
          photo_url,
          exibir_liga,
          usuarios_ligas (
            ligas (
              nome
            )
          )
        )
      `)
      .eq('ranking_id', rankingId)
      .order(sortBy, { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) throw error;
    
    // Flatten the results for easier consumption
    return data.map((entry: any, index: number) => ({
      ...entry,
      position: offset + index + 1, // Calculate position based on offset
      photo_url: entry.usuarios?.photo_url || entry.photo_url,
      exibir_liga: entry.usuarios?.exibir_liga,
      liga_nome: entry.usuarios?.usuarios_ligas?.[0]?.ligas?.nome
    }));
  },

  async getRankingByPartner(parceiroId: string) {
    // parceiro_id is being deprecated in ranking table
    // Fetch global ranking instead
    const { data: ranking } = await supabase
      .from('ranking')
      .select('id')
      .eq('tipo', 'GLOBAL')
      .maybeSingle();
    
    if (!ranking) return [];
    
    return this.getRankingUsers(ranking.id);
  },

  async initializePartnerRankings(parceiroId: string) {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const types = ['MENSAL', 'GLOBAL'];

    for (const type of types) {
      const currentYear = type === 'GLOBAL' ? 0 : year;
      const currentMonth = type === 'GLOBAL' ? 0 : month;

      const { data: existing } = await supabase
        .from('ranking')
        .select('id')
        .eq('ano', currentYear)
        .eq('mes', currentMonth)
        .eq('tipo', type)
        .maybeSingle();

      if (!existing) {
        await supabase
          .from('ranking')
          .insert([{
            ano: currentYear,
            mes: currentMonth,
            tipo: type,
            data_registro: new Date().toISOString(),
            data_atualizacao: new Date().toISOString()
          }]);
      }
    }
  },

  async updateRankings(userId: string, pontos: number, volume: number, descarteId?: string) {
    if (!descarteId) {
      console.warn("updateRankings called without descarteId. Idempotency cannot be guaranteed.");
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    try {
      // 1. Obter nome e email do usuário para garantir dados consistentes
      const { data: userProfile } = await supabase
        .from('usuarios')
        .select('nome, email')
        .eq('id', userId)
        .maybeSingle();

      const nome = userProfile?.nome || 'Usuário';
      const email = userProfile?.email || '';

      // 2. Definir os rankings que queremos manter atualizados: MENSAL, ANUAL, TOTAL e GLOBAL
      const rankingTypes = [
        { tipo: 'MENSAL', ano: year, mes: month },
        { tipo: 'ANUAL', ano: year, mes: 0 },
        { tipo: 'TOTAL', ano: 0, mes: 0 },
        { tipo: 'GLOBAL', ano: 0, mes: 0 }
      ];

      for (const rt of rankingTypes) {
        // Encontrar ou criar o registro pai na tabela `ranking`
        let { data: r } = await supabase
          .from('ranking')
          .select('id')
          .eq('tipo', rt.tipo)
          .eq('ano', rt.ano)
          .eq('mes', rt.mes)
          .maybeSingle();

        if (!r) {
          const { data: newR, error: insErr } = await supabase
            .from('ranking')
            .insert([{
              ano: rt.ano,
              mes: rt.mes,
              tipo: rt.tipo,
              data_registro: new Date().toISOString(),
              data_atualizacao: new Date().toISOString()
            }])
            .select('id')
            .maybeSingle();

          if (newR) {
            r = newR;
          } else {
            console.error(`Failed to insert ranking of type ${rt.tipo}:`, insErr);
            continue;
          }
        }

        // Encontrar ou criar o registro do usuário na tabela `ranking_usuarios`
        const { data: existingUserRank } = await supabase
          .from('ranking_usuarios')
          .select('*')
          .eq('ranking_id', r.id)
          .eq('usuario_id', userId)
          .maybeSingle();

        if (existingUserRank) {
          // Atualiza registro existente
          const newPontos = (existingUserRank.pontos || 0) + Math.round(pontos);
          const newDescartes = (existingUserRank.descartes || 0) + 1;
          const newVolume = Number(((existingUserRank.volume || 0) + volume).toFixed(4));

          await supabase
            .from('ranking_usuarios')
            .update({
              nome,
              email,
              pontos: newPontos,
              descartes: newDescartes,
              volume: newVolume,
              data_atualizacao: new Date().toISOString()
            })
            .eq('id', existingUserRank.id);
        } else {
          // Cria novo registro
          await supabase
            .from('ranking_usuarios')
            .insert([{
              ranking_id: r.id,
              usuario_id: userId,
              nome,
              email,
              pontos: Math.round(pontos),
              descartes: 1,
              volume: Number(volume.toFixed(4)),
              posicao: 999,
              mes: rt.mes,
              ano: rt.ano,
              tipo: rt.tipo,
              data_registro: new Date().toISOString(),
              data_atualizacao: new Date().toISOString()
            }]);
        }

        // Recalcular as posições para este ranking_id
        const { data: allRankedUsers } = await supabase
          .from('ranking_usuarios')
          .select('id, volume, pontos')
          .eq('ranking_id', r.id);

        if (allRankedUsers && allRankedUsers.length > 0) {
          const sorted = [...allRankedUsers].sort((a, b) => {
            if ((b.volume || 0) !== (a.volume || 0)) {
              return (b.volume || 0) - (a.volume || 0);
            }
            return (b.pontos || 0) - (a.pontos || 0);
          });

          for (let idx = 0; idx < sorted.length; idx++) {
            const sortedUser = sorted[idx];
            const newPos = idx + 1;
            await supabase
              .from('ranking_usuarios')
              .update({ posicao: newPos })
              .eq('id', sortedUser.id);
          }
        }
      }
    } catch (err) {
      console.error("Failed to update rankings fully via TypeScript:", err);
    }
  },

  // Leagues (Ligas)
  async getLeagues() {
    return this.getCached('leagues', async () => {
      const { data, error } = await supabase
        .from('ligas')
        .select('*')
        .order('nivel', { ascending: true });
      
      if (error) throw error;
      return data;
    });
  },

  async getUserLeague(userId: string) {
    const { data, error } = await supabase
      .from('usuarios_ligas')
      .select('*, ligas(*)')
      .eq('usuario_id', userId)
      .maybeSingle();
    
    if (error) throw error;

    if (data) {
      // Append current month XP
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      const periodoInicio = this.formatDateLocal(firstDay);
      const periodoFim = this.formatDateLocal(lastDay);

      const { data: ranking } = await supabase
        .from('ranking_ligas')
        .select('xp_periodo')
        .eq('usuario_id', userId)
        .eq('periodo_inicio', periodoInicio)
        .eq('periodo_fim', periodoFim)
        .maybeSingle();
      
      data.xp_periodo = ranking?.xp_periodo || 0;
    }

    return data;
  },

  async getLeagueRanking(ligaId: string, periodoInicio: string, periodoFim: string) {
    const { data, error } = await supabase
      .from('ranking_ligas')
      .select('*, usuarios(nome, photo_url, sequencia, qtd_descartes, exibir_liga)')
      .eq('liga_id', ligaId)
      .eq('periodo_inicio', periodoInicio)
      .eq('periodo_fim', periodoFim);
    
    if (error) throw error;

    // Fetch mission counts for tie-breaking
    const userIds = (data || []).map(item => item.usuario_id);
    let missionCounts: Record<string, number> = {};
    
    if (userIds.length > 0) {
      const { data: missions } = await supabase
        .from('missoes_user')
        .select('usuario_id')
        .in('usuario_id', userIds)
        .eq('concluida', true);
      
      (missions || []).forEach(m => {
        missionCounts[m.usuario_id] = (missionCounts[m.usuario_id] || 0) + 1;
      });
    }

    // Process and sort in-memory for robust tie-breaking
    // Tie-break rules:
    // 1. XP Periodo (desc)
    // 2. Data Atualizacao (asc - reached first)
    // 3. Sequencia (desc - streak)
    // 4. Mission Count (desc)
    const processedRanking = await Promise.all((data || []).map(async (item: any) => {
      // Attachment metadata
      item.missoes_concluidas = missionCounts[item.usuario_id] || 0;
      item.sequencia = item.usuarios?.sequencia || 0;

      if (item.usuarios?.photo_url && !item.usuarios.photo_url.startsWith('http')) {
        try {
          item.usuarios.photo_url = await this.getSignedUrl(item.usuarios.photo_url);
        } catch (e) {
          console.error("Error getting signed URL for ranking user photo:", item.usuarios.photo_url, e);
        }
      }
      return item;
    }));

    processedRanking.sort((a, b) => {
      // 1. XP
      if (b.xp_periodo !== a.xp_periodo) return b.xp_periodo - a.xp_periodo;
      
      // 2. Reached score first
      const timeA = new Date(a.data_atualizacao).getTime();
      const timeB = new Date(b.data_atualizacao).getTime();
      if (timeA !== timeB) return timeA - timeB;
      
      // 3. Streak
      if (b.sequencia !== a.sequencia) return b.sequencia - a.sequencia;
      
      // 4. Missions
      return b.missoes_concluidas - a.missoes_concluidas;
    });

    return processedRanking;
  },

  async getExpConfig(id: string): Promise<{ valor: number; meta_valor: number } | null> {
    try {
      const { data, error } = await supabase
        .from('config_experiencia')
        .select('valor, meta_valor')
        .eq('id', id)
        .maybeSingle();
      
      if (error || !data) return null;
      return data;
    } catch (e) {
      return null;
    }
  },

  async getExpValue(id: string, defaultValue: number): Promise<number> {
    const config = await this.getExpConfig(id);
    return config ? config.valor : defaultValue;
  },

  async updateUserXP(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('updateUserXP', args);
  },

  async checkAndProcessLeagueShift(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('checkAndProcessLeagueShift', args);
  },

  async getUserGlobalRank(userId: string) {
    return this.getCached(`user_global_rank_${userId}`, async () => {
      const { data, error } = await supabase
        .from('ranking_usuarios')
        .select('posicao')
        .eq('usuario_id', userId)
        .eq('tipo', 'GLOBAL')
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching user ranking:", error);
        return null;
      }
      
      return data?.posicao || null;
    }, 60000); // 1 minute cache
  },

  async getUserMonthlyRank(userId: string) {
    return this.getCached(`user_monthly_rank_${userId}`, async () => {
      const now = new Date();
      const month = now.getMonth() + 1; // 1-12
      const year = now.getFullYear();

      const { data, error } = await supabase
        .from('ranking_usuarios')
        .select('posicao')
        .eq('usuario_id', userId)
        .eq('tipo', 'MENSAL')
        .eq('mes', month)
        .eq('ano', year)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching user monthly ranking:", error);
        return null;
      }
      
      return data?.posicao || null;
    }, 60000); // 1 minute cache
  },

  async getUserLeagueRank(userId: string, ligaId: string) {
    return this.getCached(`user_league_rank_${userId}_${ligaId}`, async () => {
      try {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        
        const periodoInicio = this.formatDateLocal(firstDay);
        const periodoFim = this.formatDateLocal(lastDay);

        const rankingList = await this.getLeagueRanking(ligaId, periodoInicio, periodoFim);
        if (!rankingList || rankingList.length === 0) return 1;

        const index = rankingList.findIndex((item: any) => item.usuario_id === userId);
        return index !== -1 ? index + 1 : 1;
      } catch (err) {
        console.error("Error fetching user league rank:", err);
        return 1;
      }
    }, 60000); // 1 minute cache
  },

  async getUserFrequencyRank(userId: string) {
    return this.getCached(`user_freq_rank_${userId}`, async () => {
      const { data: user, error: userError } = await supabase
        .from('usuarios')
        .select('qtd_descartes')
        .eq('id', userId)
        .single();
      
      if (userError || !user) return null;

      const { count, error } = await supabase
        .from('usuarios')
        .select('*', { count: 'exact', head: true })
        .gt('qtd_descartes', user.qtd_descartes || 0);
      
      if (error) throw error;
      return (count || 0) + 1;
    }, 60000); // 1 minute cache
  },

  async getGlobalRanking() {
    return this.getCached('global_ranking', async () => {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, photo_url, qtd_kg_desc')
        .order('qtd_kg_desc', { ascending: false })
        .limit(50);
      
      if (error) throw error;

      // Process photos
      const processed = await Promise.all(data.map(async (u: any) => {
        const photo = u.photo_url;
        if (photo && !photo.startsWith('http')) {
          try {
            u.photo_url = await this.getSignedUrl(photo);
          } catch (e) {
            console.error("Error getting signed URL for ranking user:", photo, e);
          }
        } else {
          u.photo_url = photo;
        }
        return u;
      }));

      return processed;
    }, 60000); // 1 minute cache for rankings
  },

  async getFrequencyRanking() {
    return this.getCached('frequency_ranking', async () => {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, photo_url, qtd_descartes')
        .order('qtd_descartes', { ascending: false })
        .limit(50);
      
      if (error) throw error;

      // Process photos
      const processed = await Promise.all(data.map(async (u: any) => {
        const photo = u.photo_url;
        if (photo && !photo.startsWith('http')) {
          try {
            u.photo_url = await this.getSignedUrl(photo);
          } catch (e) {
            console.error("Error getting signed URL for ranking user:", photo, e);
          }
        } else {
          u.photo_url = photo;
        }
        return u;
      }));

      return processed;
    }, 60000); // 1 minute cache for rankings
  },

  // Ecopontos (Map)
  async getEcopontos() {
    return this.getCached('ecopontos', async () => {
      const { data, error } = await supabase
        .from('ecopontos')
        .select('*, tipo_unidade, grupo')
        .order('nome');
      
      if (error) throw error;
      return data;
    });
  },

  async getAttendants() {
    const { data, error } = await supabase
      .from('usuarios_admin')
      .select('*, usuarios(nome, email)')
      .eq('setor', 'Atendente');
    
    if (error) throw error;
    return data;
  },

  async getEcopontoMaterials(ecopontoId: string) {
    return this.getCached(`ecoponto_materials_${ecopontoId}`, async () => {
      const { data, error } = await supabase
        .from('ecopontos_materiais_aceitos')
        .select('*')
        .eq('ecoponto_id', ecopontoId)
        .order('material');
      
      if (error) throw error;
      return data;
    });
  },

  async getEcopontoHomologatedPartners(ecopontoId: string) {
    const { data, error } = await supabase
      .from('ecopontos_parceiros_homol')
      .select('parceiro_id, parceiros(*)')
      .eq('ecoponto_id', ecopontoId);

    if (error) throw error;

    return (data || []).map(h => {
      const p = h.parceiros;
      return Array.isArray(p) ? p[0] : p;
    }).filter(Boolean);
  },

  // Missions
  async syncMissionStatuses() {
    const now = new Date();
    const nowIso = now.toISOString();

    // 1. Get missions that might need status update
    // Missions that are 'Programada' but should be 'Ativa'
    // Missions that are 'Ativa' but should be 'Encerrada'
    const { data: missions, error } = await supabase
      .from('missoes')
      .select('id, status, data_inicio, data_fim')
      .in('status', ['Programada', 'Ativa', 'ATIVA', 'Ativo', 'ATIVO']);

    if (error || !missions) return;

    for (const mission of missions) {
      let newStatus = mission.status;

      if (mission.status === 'Programada') {
        const start = mission.data_inicio ? new Date(`${mission.data_inicio.split('T')[0]}T00:00:00`) : null;
        const end = mission.data_fim ? new Date(`${mission.data_fim.split('T')[0]}T23:59:59.999`) : null;
        
        if ((!start || now >= start) && (!end || now <= end)) {
          newStatus = 'Ativa';
        } else if (end && now > end) {
          newStatus = 'Encerrada';
        }
      } else if (mission.status === 'Ativa' || mission.status === 'ATIVA' || mission.status === 'Ativo' || mission.status === 'ATIVO') {
        const end = mission.data_fim ? new Date(`${mission.data_fim.split('T')[0]}T23:59:59.999`) : null;
        if (end && now > end) {
          newStatus = 'Encerrada';
        }
      }

      if (newStatus !== mission.status) {
        try {
          await supabase
            .from('missoes')
            .update({ status: newStatus, data_atualizacao: nowIso })
            .eq('id', mission.id);
          console.log(`[Sync] Mission ${mission.id} status changed from ${mission.status} to ${newStatus}`);
        } catch (err) {
          console.warn(`[Sync] Failed to update mission ${mission.id} status (might be restricted by RLS custom rules):`, err);
        }
      }
    }
  },

  async getMissions() {
    // Sync statuses before fetching
    try {
      await this.syncMissionStatuses();
    } catch (e) {
      console.error("Error syncing mission statuses:", e);
    }

    return this.getCached('missions', async () => {
      const { data, error } = await supabase
        .from('missoes')
        .select('*, missoes_eventos(*), medalhas(*)')
        .or('status.eq.Ativa,status.eq.ATIVA,status.eq.Ativo,status.eq.ATIVO')
        .order('nivel_jornada', { ascending: true });
      
      if (error) throw error;

      // Process URLs
      const processedMissions = await Promise.all((data || []).map(async (m: any) => {
        const shouldRefresh = (url: string) => {
          if (!url) return false;
          return !url.startsWith('http') || url.includes('/storage/v1/object/sign/');
        };

        // Mission Banner Image
        if (m.imagem_missao && shouldRefresh(m.imagem_missao)) {
          try {
            m.imagem_missao = await this.getSignedUrl(m.imagem_missao);
          } catch (e) {
            console.error("Error getting signed URL for mission image:", m.imagem_missao, e);
          }
        }

        // Medal Image from linked table
        const medal = m.medalhas && m.medalhas.length > 0 ? m.medalhas[0] : null;
        if (medal) {
          m.imagem_medalha = medal.imagem_medalha;
        }

        if (m.imagem_medalha && shouldRefresh(m.imagem_medalha)) {
          try {
            m.imagem_medalha = await this.getSignedUrl(m.imagem_medalha);
          } catch (e) {
            console.error("Error getting signed URL for medal image:", m.imagem_medalha, e);
          }
        }
        return m;
      }));

      // Sort missions explicitly in ascending order of journey level (nivel_jornada)
      processedMissions.sort((a: any, b: any) => {
        const valA = Number(a.nivel_jornada) || 0;
        const valB = Number(b.nivel_jornada) || 0;
        return valA - valB;
      });

      return processedMissions;
    });
  },

  async getMissionEvents(missaoId: string) {
    const { data, error } = await supabase
      .from('missoes_eventos')
      .select('*')
      .eq('missao_id', missaoId);
    
    if (error) throw error;
    return data;
  },

  async getUserMissions(userId: string) {
    const { data, error } = await supabase
      .from('missoes_user')
      .select('*')
      .eq('usuario_id', userId);
    
    if (error) throw error;
    return data;
  },

  async getUserMissionEvents(userId: string, missaoId: string) {
    const { data, error } = await supabase
      .from('missoes_user_eventos')
      .select('*, missoes_user!inner(*)')
      .eq('missoes_user.usuario_id', userId)
      .eq('missoes_user.missao_id', missaoId);
    
    if (error) throw error;
    return data;
  },

  // Institutional
  async getInstitutionalContent() {
    return this.getCached('institutional_content', async () => {
      const { data, error } = await supabase
        .from('conteudo_institucional')
        .select('*')
        .order('ordem', { ascending: true });
      
      if (error) throw error;

      // Process URLs
      const processedContent = await Promise.all(data.map(async (item: any) => {
        if (item.imagem_url && !item.imagem_url.startsWith('http')) {
          try {
            item.imagem_url = await this.getSignedUrl(item.imagem_url);
          } catch (e) {
            console.error("Error getting signed URL for institutional image:", item.imagem_url, e);
          }
        }
        return item;
      }));

      return processedContent;
    });
  },

  // Partners / Programs
  async getActivePartners() {
    return this.getCached('active_partners', async () => {
      const { data, error } = await supabase
        .from('parceiros')
        .select('*')
        .eq('status_ativo', true);
      
      if (error) throw error;

      const shouldRefresh = (url: string) => {
        if (!url) return false;
        return !url.startsWith('http') || url.includes('/storage/v1/object/sign/');
      };

      // Process URLs
      const processed = await Promise.all((data || []).map(async (p: any) => {
        if (p.logo_parceiro && shouldRefresh(p.logo_parceiro)) {
          try {
            p.logo_parceiro = await this.getSignedUrl(p.logo_parceiro);
          } catch (e) {
            console.error("Error getting signed URL for partner logo:", p.logo_parceiro, e);
          }
        }
        if (p.img_capa_parceiro && shouldRefresh(p.img_capa_parceiro)) {
          try {
            p.img_capa_parceiro = await this.getSignedUrl(p.img_capa_parceiro);
          } catch (e) {
            console.error("Error getting signed URL for partner cover image:", p.img_capa_parceiro, e);
          }
        }
        return p;
      }));

      return processed;
    });
  },

  // Education
  async getEducationVideos() {
    return this.getCached('education_videos', async () => {
      const { data, error } = await supabase
        .from('conteudos_educacionais')
        .select('*')
        .eq('status_ativo', true)
        .order('data_registro', { ascending: false });
      
      if (error) throw error;
      return data;
    });
  },

  // Impact Calculation
  async getUserTopPartnerTheme(userId?: string, fallbackPartnerId?: string) {
    if (!userId && !fallbackPartnerId) return null;

    return this.getCached(`user_top_partner_theme_${userId || fallbackPartnerId}`, async () => {
      try {
        let topPartnerId: string | null = null;
        let totalPartnerDiscards = 0;

        if (userId) {
          // 1. Query user's discards with parceiro_id and ecoponto_id
          const { data: discards, error: discardsErr } = await supabase
            .from('descartes')
            .select('parceiro_id, ecoponto_id, qtd_peso_gramas')
            .eq('usuario_id', userId);

          if (!discardsErr && discards && discards.length > 0) {
            const partnerStats: Record<string, { count: number; weight: number }> = {};
            const missingEcopontoIds: string[] = [];

            for (const d of discards) {
              if (d.parceiro_id) {
                if (!partnerStats[d.parceiro_id]) partnerStats[d.parceiro_id] = { count: 0, weight: 0 };
                partnerStats[d.parceiro_id].count += 1;
                partnerStats[d.parceiro_id].weight += Number(d.qtd_peso_gramas) || 0;
              } else if (d.ecoponto_id) {
                missingEcopontoIds.push(d.ecoponto_id);
              }
            }

            // Resolve missing parceiro_id via ecopontos
            if (missingEcopontoIds.length > 0) {
              const uniqueEcopontos = Array.from(new Set(missingEcopontoIds));
              
              const { data: ecopontosData } = await supabase
                .from('ecopontos')
                .select('id, parceiro_id')
                .in('id', uniqueEcopontos);

              const ecoMap = new Map<string, string>();
              ecopontosData?.forEach(e => {
                if (e.parceiro_id) ecoMap.set(e.id, e.parceiro_id);
              });

              const unmapped = uniqueEcopontos.filter(id => !ecoMap.has(id));
              if (unmapped.length > 0) {
                const { data: links } = await supabase
                  .from('ecopontos_parceiros_homol')
                  .select('ecoponto_id, parceiro_id')
                  .in('ecoponto_id', unmapped);
                links?.forEach(l => {
                  if (l.parceiro_id) ecoMap.set(l.ecoponto_id, l.parceiro_id);
                });
              }

              for (const d of discards) {
                if (!d.parceiro_id && d.ecoponto_id && ecoMap.has(d.ecoponto_id)) {
                  const pId = ecoMap.get(d.ecoponto_id)!;
                  if (!partnerStats[pId]) partnerStats[pId] = { count: 0, weight: 0 };
                  partnerStats[pId].count += 1;
                  partnerStats[pId].weight += Number(d.qtd_peso_gramas) || 0;
                }
              }
            }

            // Find partner with the highest discard count (and weight as tie breaker)
            const sorted = Object.entries(partnerStats).sort((a, b) => {
              if (b[1].count !== a[1].count) return b[1].count - a[1].count;
              return b[1].weight - a[1].weight;
            });

            if (sorted.length > 0) {
              topPartnerId = sorted[0][0];
              totalPartnerDiscards = sorted[0][1].count;
            }
          }
        }

        // 2. Fallback to direct profile partner or programs
        if (!topPartnerId && fallbackPartnerId) {
          topPartnerId = fallbackPartnerId;
        }

        if (!topPartnerId && userId) {
          const { data: userPrograms } = await supabase
            .from('usuarios_programas')
            .select('parceiro_id')
            .eq('usuario_id', userId)
            .limit(1)
            .maybeSingle();

          if (userPrograms?.parceiro_id) {
            topPartnerId = userPrograms.parceiro_id;
          }
        }

        // 3. Fetch the partner details
        if (topPartnerId) {
          const { data: partner, error: pErr } = await supabase
            .from('parceiros')
            .select('id, nome_parceiro, nome_projeto, cor_principal, cor_secundaria, logo_parceiro, img_capa_parceiro, status_ativo')
            .eq('id', topPartnerId)
            .maybeSingle();

          if (!pErr && partner) {
            let logo = partner.logo_parceiro;
            if (logo && !logo.startsWith('http')) {
              try {
                logo = await this.getSignedUrl(logo);
              } catch (e) {
                console.error("Error getting signed URL for partner logo in theme:", partner.id, e);
              }
            }

            // Normalize hex/css colors
            const normalizeColor = (col: string | undefined, fallback: string) => {
              if (!col || typeof col !== 'string') return fallback;
              const trimmed = col.trim();
              if (trimmed.startsWith('#') || trimmed.startsWith('rgb') || trimmed.startsWith('hsl')) return trimmed;
              if (/^[0-9A-Fa-f]{6}$/.test(trimmed) || /^[0-9A-Fa-f]{3}$/.test(trimmed)) return `#${trimmed}`;
              return fallback;
            };

            return {
              id: partner.id,
              nome_parceiro: partner.nome_parceiro || 'Parceiro Sustentável',
              nome_projeto: partner.nome_projeto || '',
              cor_principal: normalizeColor(partner.cor_principal, '#10b981'),
              cor_secundaria: normalizeColor(partner.cor_secundaria, '#0f172a'),
              logo_parceiro: logo || null,
              total_descartes: totalPartnerDiscards
            };
          }
        }

        return null;
      } catch (err) {
        console.error("Error getting user top partner theme:", err);
        return null;
      }
    }, 60000);
  },

  async getUserImpact(userId: string) {
    const { data, error } = await supabase
      .from('descartes')
      .select('qtd_peso_gramas')
      .eq('usuario_id', userId);
    
    if (error) throw error;
    
    const totalWeight = data.reduce((acc, curr) => acc + (Number(curr.qtd_peso_gramas) || 0), 0) / 1000;
    
    // Industrial averages
    return {
      weight: totalWeight.toFixed(1),
      co2: (totalWeight * 2.5).toFixed(1),
      water: (totalWeight * 25).toFixed(0),
      energy: (totalWeight * 15).toFixed(0),
      tv: (totalWeight * 2.4).toFixed(0),
      gas: (totalWeight * 0.84).toFixed(1),
      km: (totalWeight * 7.6).toFixed(0),
      ore: (totalWeight * 3).toFixed(0),
      sand: (totalWeight * 4.4).toFixed(0),
      petroleum: (totalWeight * 1.7).toFixed(1),
      trees: (totalWeight * 0.01).toFixed(2)
    };
  },

  async getGlobalImpact() {
    const { data, error } = await supabase
      .from('descartes')
      .select('qtd_peso_gramas');
    
    if (error) throw error;
    
    const totalWeightKg = data.reduce((acc, curr) => acc + (Number(curr.qtd_peso_gramas) || 0), 0) / 1000;
    
    return {
      weight: totalWeightKg.toFixed(1),
      co2: (totalWeightKg * 2.5).toFixed(1),
      water: (totalWeightKg * 25).toFixed(0),
      energy: (totalWeightKg * 15).toFixed(0),
      trees: (totalWeightKg * 0.01).toFixed(2)
    };
  },

  async getRewards() {
    return this.getCached('rewards', async () => {
      const { data, error } = await supabase
        .from('recompensas')
        .select('*, lojas(loja_nome, loja_logo, parceiro_id)')
        .eq('status_ativo', true)
        .eq('status', 'APROVADO')
        .gt('quantidade', 0);
      
      if (error) throw error;

      // Process signed URLs for reward images and store logos
      const processed = await Promise.all(data.map(async (r: any) => {
        // Prioritize 'imagem' column as per user request
        const imagePath = r.imagem || r.imagem_url;
        if (imagePath) {
          if (!imagePath.startsWith('http')) {
            try {
              const signedUrl = await this.getSignedUrl(imagePath);
              r.imagem = signedUrl;
              r.imagem_url = signedUrl;
            } catch (e) {
              console.error("Error getting signed URL for reward image:", imagePath, e);
              r.imagem = null;
              r.imagem_url = null;
            }
          } else {
            r.imagem = imagePath;
            r.imagem_url = imagePath;
          }
        } else {
          r.imagem = null;
          r.imagem_url = null;
        }
        if (r.lojas?.loja_logo && !r.lojas.loja_logo.startsWith('http')) {
          try {
            r.lojas.loja_logo = await this.getSignedUrl(r.lojas.loja_logo);
          } catch (e) {
            console.error("Error getting signed URL for store logo:", r.lojas.loja_logo, e);
          }
        }
        return r;
      }));

      return processed;
    }, 120000); // 2 minutes for rewards
  },

  async getStores() {
    return this.getCached('stores', async () => {
      let query = supabase.from('lojas').select('*, parceiros(nome_parceiro)');
      const { data, error } = await query;
      
      let stores = data;
      if (error) {
        // Fallback without join if foreign key has issue
        const { data: fallbackData, error: fallbackError } = await supabase.from('lojas').select('*');
        if (fallbackError) throw fallbackError;
        stores = fallbackData;
      }

      // Filter active (exclude explicitly deactivated ones)
      const activeStores = (stores || []).filter((s: any) => s.status_ativo !== false && s.status !== 'INATIVO' && s.status !== 'DESATIVADO');

      const processed = await Promise.all(activeStores.map(async (s: any) => {
        // Prioritize 'loja_logo' column as per user request
        if (s.loja_logo && !s.loja_logo.startsWith('http')) {
          try {
            s.loja_logo = await this.getSignedUrl(s.loja_logo);
          } catch (e) {
            console.error("Error getting signed URL for store logo:", s.loja_logo, e);
          }
        }
        return s;
      }));

      return processed;
    });
  },

  async deleteLoja(lojaId: string) {
    try {
      // 1. Delete associated collaborators
      try {
        await supabase.from('loja_colaboradores').delete().eq('loja_id', lojaId);
      } catch (e) {
        console.warn("Could not delete loja_colaboradores:", e);
      }

      // 2. Delete rewards belonging to this store
      try {
        await supabase.from('recompensas').delete().eq('loja_id', lojaId);
      } catch (e) {
        console.warn("Could not delete recompensas for loja:", e);
      }

      // 3. Unlink redemptions referencing this store
      try {
        await supabase.from('resgates').update({ loja_id: null }).eq('loja_id', lojaId);
      } catch (e) {
        console.warn("Could not unlink resgates for loja:", e);
      }

      // 4. Delete the store itself
      const { error } = await supabase.from('lojas').delete().eq('id', lojaId);
      if (error) throw error;

      this.clearCache('stores');
      this.clearCache('rewards');
      return true;
    } catch (err: any) {
      this.logError(err, 'deleteLoja', undefined, `Failed to delete loja ${lojaId}`);
      throw err;
    }
  },

  // Checklist
  async getChecklistItems() {
    return this.getCached('checklist_items', async () => {
      const { data, error } = await supabase
        .from('checklist_itens')
        .select('*')
        .order('ordem', { ascending: true });
      
      if (error) throw error;
      return data;
    });
  },

  async saveChecklistExecution(userId: string, ecopontoId: string, periodo: string, responses: { item_id: string, resposta: boolean, observacao?: string }[], photoFile?: File) {
    // 1. Upload photo if provided
    let photoUrl = null;
    if (photoFile) {
      photoUrl = await this.uploadFile(userId, 'checklists', photoFile);
    }

    // 2. Create/Update execution record
    const today = this.formatDateLocal(new Date());
    const record: any = {
      atendente_id: userId,
      ecoponto_id: ecopontoId,
      periodo: periodo,
      foto_obrigatoria: !!photoFile,
      finalizado: true,
      data_execucao: today
    };

    if (photoUrl) {
      record.foto_url = photoUrl;
    }

    // Check if already exists for today
    const { data: existing } = await supabase
      .from('checklist_execucao')
      .select('id')
      .eq('atendente_id', userId)
      .eq('ecoponto_id', ecopontoId)
      .eq('periodo', periodo)
      .eq('data_execucao', today)
      .maybeSingle();

    let execution;
    if (existing) {
      const { data: updated, error: updateError } = await supabase
        .from('checklist_execucao')
        .update(record)
        .eq('id', existing.id)
        .select()
        .single();
      if (updateError) throw updateError;
      execution = updated;
    } else {
      const { data: inserted, error: insertError } = await supabase
        .from('checklist_execucao')
        .insert([record])
        .select()
        .single();
      if (insertError) throw insertError;
      execution = inserted;
    }

    // 3. Save responses - Delete old ones first to avoid duplicates
    await supabase
      .from('checklist_respostas')
      .delete()
      .eq('execucao_id', execution.id);

    const responseRecords = responses.map(r => ({
      execucao_id: execution.id,
      item_id: r.item_id,
      realizado: r.resposta,
      descricao: (r as any).descricao || r.observacao,
      atendente_id: userId
    }));

    const { error: respError } = await supabase
      .from('checklist_respostas')
      .insert(responseRecords);
    
    if (respError) throw respError;

    return execution;
  },

  async getChecklistResponses(userId: string, ecopontoId: string, periodo: string) {
    const today = this.formatDateLocal(new Date());
    const { data, error } = await supabase
      .from('checklist_execucao')
      .select(`
        *,
        respostas:checklist_respostas(item_id, realizado)
      `)
      .eq('atendente_id', userId)
      .eq('ecoponto_id', ecopontoId)
      .eq('periodo', periodo)
      .eq('data_execucao', today)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  },

  async getTodayChecklistStatus(userId: string, ecopontoId: string) {
    const today = this.formatDateLocal(new Date());
    const { data, error } = await supabase
      .from('checklist_execucao')
      .select('periodo')
      .eq('atendente_id', userId)
      .eq('ecoponto_id', ecopontoId)
      .eq('data_execucao', today);
    
    if (error) throw error;
    return data.map(d => d.periodo);
  },

  async getOperationalPerformance(ecopontoId: string, atendenteId: string) {
    const now = new Date();
    
    // Start of today
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const todayStr = startOfToday.toISOString();
    
    // Start of week (Sunday)
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const weekStr = startOfWeek.toISOString();

    // Start of month
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const monthStr = startOfMonth.toISOString();

    // 1. Fetch Discards for the ecoponto
    const { data: discards, error: discardsError } = await supabase
      .from('descartes')
      .select('data_registro, qtd_peso_gramas')
      .eq('ecoponto_id', ecopontoId)
      .gte('data_registro', monthStr);

    if (discardsError) throw discardsError;

    const stats = {
      today: { count: 0, weight: 0 },
      week: { count: 0, weight: 0 },
      month: { count: 0, weight: 0 }
    };

    discards?.forEach(d => {
      const dDate = new Date(d.data_registro);
      const dWeight = (d.qtd_peso_gramas || 0) / 1000; // Convert to kg

      // Month (all in query are this month)
      stats.month.count++;
      stats.month.weight += dWeight;

      // Week
      if (dDate >= startOfWeek) {
        stats.week.count++;
        stats.week.weight += dWeight;
      }

      // Today
      if (dDate >= startOfToday) {
        stats.today.count++;
        stats.today.weight += dWeight;
      }
    });

    // 2. Fetch NPS (Average for the ecoponto or attendant)
    // The NPS table has atendente_id. We'll fetch for the attendant.
    const { data: npsData, error: npsError } = await supabase
      .from('NPS')
      .select('nota')
      .eq('atendente_id', atendenteId);

    let avgNps = 0;
    if (npsData && npsData.length > 0) {
      const sum = npsData.reduce((acc, curr) => acc + curr.nota, 0);
      avgNps = sum / npsData.length;
    }

    return {
      today: stats.today,
      week: stats.week,
      month: stats.month,
      avgNps: avgNps.toFixed(1)
    };
  },

  async getMaterialAccumulatedWeight(userId: string, materialId: string) {
    const { data, error } = await supabase
      .from('descartes_materiais')
      .select('gramas')
      .eq('material_id', materialId)
      .filter('descarte_id', 'in', 
        supabase.from('descartes').select('id').eq('usuario_id', userId)
      );
    
    if (error) {
      // Fallback if subquery fails or is not supported in this way
      const { data: discards } = await supabase.from('descartes').select('id').eq('usuario_id', userId);
      if (!discards || discards.length === 0) return 0;
      
      const discardIds = discards.map(d => d.id);
      const { data: items, error: itemsError } = await supabase
        .from('descartes_materiais')
        .select('gramas')
        .eq('material_id', materialId)
        .in('descarte_id', discardIds);
      
      if (itemsError) throw itemsError;
      return items.reduce((acc, item) => acc + Number(item.gramas || 0), 0);
    }

    return data.reduce((acc, item) => acc + Number(item.gramas || 0), 0);
  },

  async getUserMaterialProgress(userId: string) {
    const { data: materials } = await supabase.from('materiais').select('*').eq('status_ativo', true);
    if (!materials) return [];

    const progress = await Promise.all(materials.map(async (m: any) => {
      const accumulated = await this.getMaterialAccumulatedWeight(userId, m.id);
      return {
        id: m.id,
        nome: m.nome_material,
        icone: m.icone_url,
        accumulated: accumulated,
        progress: (accumulated % 1000) / 10, // Percentage of the current kg
        remaining: 1000 - (accumulated % 1000),
        totalKgs: Math.floor(accumulated / 1000)
      };
    }));

    return progress;
  },

  async getAllUsersMaterialProgress() {
    const { data: users } = await supabase.from('usuarios').select('id, nome').eq('tipo_perfil', 'Usuário');
    if (!users) return [];

    const allProgress = await Promise.all(users.map(async (u: any) => {
      const progress = await this.getUserMaterialProgress(u.id);
      return {
        userId: u.id,
        userName: u.nome,
        materials: progress
      };
    }));

    return allProgress;
  },

  async seedChecklistItems(items: { descricao: string, periodo: string, ordem: number, obrigatorio?: boolean }[]) {
    // Try to detect if we should use old or new column names
    let { error } = await supabase
      .from('checklist_itens')
      .insert(items);
    
    if (error && (error.message.includes('column "descricao" does not exist') || error.code === '42703')) {
      // Fallback to old names
      const oldItems = items.map(i => ({
        texto: i.descricao,
        categoria: i.periodo,
        ordem: i.ordem,
        obrigatorio: i.obrigatorio
      }));
      const retry = await supabase
        .from('checklist_itens')
        .insert(oldItems);
      error = retry.error;
    }

    if (error) throw error;
    this.invalidateCache('checklist_items');
  },

  async getAllChecklistExecutions() {
    const { data, error } = await supabase
      .from('checklist_execucao')
      .select(`
        *,
        usuarios!atendente_id(nome),
        ecopontos(nome),
        checklist_respostas(
          *,
          checklist_itens(*)
        )
      `)
      .order('data_execucao', { ascending: false });
    
    if (error) throw error;

    // Process to include signed URLs for photos
    const processed = await Promise.all(data.map(async (exec: any) => {
      if (exec.foto_url && !exec.foto_url.startsWith('http')) {
        try {
          exec.foto_url = await this.getSignedUrl(exec.foto_url);
        } catch (e) {
          console.error("Error getting signed URL for checklist photo:", exec.id, e);
        }
      }
      return exec;
    }));

    return processed;
  },

  async getDashboardStats(period: string = 'Hoje', location: string = 'Brasil') {
    return this.getCached(`dashboard_stats_${period}_${location}`, async () => {
      const now = new Date();
      const today = this.formatDateLocal(now);
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    // Helper for date ranges
    const getDateRange = (p: string) => {
      const start = new Date();
      const end = new Date();
      
      if (p === 'Hoje') {
        start.setHours(0, 0, 0, 0);
      } else if (p === 'Esta Semana') {
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
      } else if (p === 'Este Mês') {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
      } else if (p === 'Este Ano') {
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);
      } else {
        start.setFullYear(2000, 0, 1); // All period
      }
      return { start: start.toISOString(), end: end.toISOString() };
    };

    const currentRange = getDateRange(period);
    
    // Previous range for trends
    const getPreviousRange = (p: string) => {
      const start = new Date();
      const end = new Date();
      const current = getDateRange(p);
      const currentStart = new Date(current.start);
      const currentEnd = new Date(current.end);
      const diff = currentEnd.getTime() - currentStart.getTime();
      
      const prevStart = new Date(currentStart.getTime() - diff);
      const prevEnd = new Date(currentStart.getTime());
      
      return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
    };
    const prevRange = getPreviousRange(period);

    // Build queries with filters
    let discardsQuery = supabase.from('descartes').select('id_machine, qtd_peso_gramas, ecoponto_id, data_registro, ecopontos!inner(uf)');
    let prevDiscardsQuery = supabase.from('descartes').select('id_machine, qtd_peso_gramas, ecoponto_id, data_registro, ecopontos!inner(uf)');
    
    // Period specific queries with location filter
    let todayQuery = supabase.from('descartes').select('id_machine, qtd_peso_gramas, ecopontos!inner(uf)').gte('data_registro', getDateRange('Hoje').start);
    let weekQuery = supabase.from('descartes').select('id_machine, qtd_peso_gramas, ecopontos!inner(uf)').gte('data_registro', getDateRange('Esta Semana').start);
    let monthQuery = supabase.from('descartes').select('id_machine, qtd_peso_gramas, ecopontos!inner(uf)').gte('data_registro', getDateRange('Este Mês').start);

    if (location !== 'Brasil') {
      discardsQuery = discardsQuery.eq('ecopontos.uf', location);
      prevDiscardsQuery = prevDiscardsQuery.eq('ecopontos.uf', location);
      todayQuery = todayQuery.eq('ecopontos.uf', location);
      weekQuery = weekQuery.eq('ecopontos.uf', location);
      monthQuery = monthQuery.eq('ecopontos.uf', location);
    }
    
    const [
      { count: pendingUsers },
      { count: pendingEcopontos },
      { count: pendingExchanges },
      { count: pendingReports },
      { count: pendingLojistasStores },
      { count: pendingLojistasRewards },
      { data: discards },
      { data: prevDiscards },
      { data: ecopontos },
      { count: totalUsers },
      { count: activeUsers },
      { count: checkinsToday },
      { count: totalPosts },
      { count: totalLikes },
      { count: totalComments },
      { count: totalMachines },
      { count: totalPartners },
      { count: activeProducts },
      { data: trocas },
      { count: usersOnline },
      { count: completeUsers },
      { data: trendingPosts },
      // Specific period stats for "Fluxo de Descartes"
      { data: todayDiscards },
      { data: weekDiscards },
      { data: monthDiscards },
      { count: usersDuringCurrent },
      { count: usersDuringPrev }
    ] = await Promise.all([
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).in('tipo_perfil', [UserProfile.COLLECTOR, UserProfile.COMPANY]).eq('status_aprovacao', 'Pendente'),
      supabase.from('ecopontos').select('*', { count: 'exact', head: true }).eq('status_ativo', false),
      supabase.from('resgates').select('*', { count: 'exact', head: true }).eq('status', 'ANALISE'),
      supabase.from('wastein_post_denuncias').select('*', { count: 'exact', head: true }).eq('resolvido', false),
      supabase.from('solicitacoes_lojistas').select('*', { count: 'exact', head: true }).eq('status', 'ANALISE'),
      supabase.from('recompensas').select('*', { count: 'exact', head: true }).eq('status', 'ANALISE'),
      discardsQuery.gte('data_registro', currentRange.start).lte('data_registro', currentRange.end),
      prevDiscardsQuery.gte('data_registro', prevRange.start).lte('data_registro', prevRange.end),
      supabase.from('ecopontos').select('id, nome, tipo_unidade, status_ativo, uf'),
      supabase.from('usuarios').select('*', { count: 'exact', head: true }),
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).gte('ultima_coleta', thirtyDaysAgo),
      supabase.from('checkins').select('*', { count: 'exact', head: true }).eq('data_checkin', today),
      supabase.from('wastein_posts').select('*', { count: 'exact', head: true }),
      supabase.from('wastein_post_curtidas').select('*', { count: 'exact', head: true }),
      supabase.from('wastein_post_comentarios').select('*', { count: 'exact', head: true }),
      supabase.from('machines').select('*', { count: 'exact', head: true }),
      supabase.from('parceiros').select('*', { count: 'exact', head: true }),
      supabase.from('recompensas').select('*', { count: 'exact', head: true }).eq('status_ativo', true),
      supabase.from('resgates').select('recompensas(nome)'),
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).gte('ultimo_acesso', fifteenMinutesAgo),
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).not('cidade_usuario', 'is', null).not('uf_usuario', 'is', null),
      supabase.from('wastein_posts').select('id, conteudo, wastein_post_curtidas(count), wastein_post_comentarios(count)').limit(5),
      // Specific period queries
      todayQuery,
      weekQuery,
      monthQuery,
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).gte('data_registro', currentRange.start).lte('data_registro', currentRange.end),
      supabase.from('usuarios').select('*', { count: 'exact', head: true }).gte('data_registro', prevRange.start).lte('data_registro', prevRange.end)
    ]);

    // Calculate volume and top material
    const totalVolume = (discards?.reduce((acc, curr) => acc + Number(curr.qtd_peso_gramas), 0) || 0) / 1000000; // Convert to TON
    const prevVolume = (prevDiscards?.reduce((acc, curr) => acc + Number(curr.qtd_peso_gramas), 0) || 0) / 1000000; // Convert to TON
    
    const volumeTrend = prevVolume > 0 ? Math.round(((totalVolume - prevVolume) / prevVolume) * 100) : 0;
    const countTrend = (prevDiscards?.length || 0) > 0 ? Math.round(((discards?.length || 0) - (prevDiscards?.length || 0)) / (prevDiscards?.length || 0) * 100) : 0;

    // Fetch top material from descartes_materiais
    const { data: itemsData } = await supabase
      .from('descartes_materiais')
      .select('material')
      .gte('data_descarte', getDateRange(period).start)
      .lte('data_descarte', getDateRange(period).end);

    const materialCounts: Record<string, number> = {};
    itemsData?.forEach(d => {
      if (d.material) {
        materialCounts[d.material] = (materialCounts[d.material] || 0) + 1;
      }
    });
    const topMaterial = Object.entries(materialCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // Ecopontos Indicators
    const ecopontoWeight: Record<string, number> = {};
    const ecopontosWithDiscards = new Set();

    discards?.forEach(d => {
      ecopontoWeight[d.ecoponto_id] = (ecopontoWeight[d.ecoponto_id] || 0) + Number(d.qtd_peso_gramas);
      ecopontosWithDiscards.add(d.ecoponto_id);
    });

    const topEcopontoId = Object.entries(ecopontoWeight).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topEcopontoName = ecopontos?.find(e => e.id === topEcopontoId)?.nome || 'N/A';
    const topEcopontoVolume = topEcopontoId ? (ecopontoWeight[topEcopontoId] / 1000000).toFixed(3) : '0.000';
    const zeroDiscardCount = (ecopontos?.filter(e => e.status_ativo && !ecopontosWithDiscards.has(e.id)).length) || 0;

    // Machines Indicators
    const machineDiscards = discards?.filter(d => d.id_machine !== null) || [];
    const machineTotalWeight = machineDiscards.reduce((acc, curr) => acc + Number(curr.qtd_peso_gramas), 0);
    const machineAvgWeight = machineDiscards.length > 0 ? (machineTotalWeight / machineDiscards.length).toFixed(1) : '0.0';
    
    const machineUsageCounts: Record<string, number> = {};
    machineDiscards.forEach(d => {
      if (d.id_machine) {
        machineUsageCounts[d.id_machine] = (machineUsageCounts[d.id_machine] || 0) + 1;
      }
    });
    const topMachineId = Object.entries(machineUsageCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topMachineName = topMachineId || 'N/A';

    // Marketplace Indicators
    const exchangeCounts: Record<string, number> = {};
    trocas?.forEach((t: any) => {
      const rewardName = t.recompensas?.nome || 'N/A';
      exchangeCounts[rewardName] = (exchangeCounts[rewardName] || 0) + 1;
    });
    const topExchange = Object.entries(exchangeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // User Growth Trend
    const userTrend = (prevRange.start === new Date(2000, 0, 1).toISOString()) 
      ? 0 
      : (usersDuringPrev || 0) > 0 
        ? Math.round(((usersDuringCurrent || 0) - (usersDuringPrev || 0)) / (usersDuringPrev || 0) * 100) 
        : 0;

    // Environmental Impact Trends
    const co2Trend = volumeTrend; // CO2 is proportional to volume
    const avgCO2PerUser = totalUsers > 0 ? (totalVolume * 2.5) / totalUsers : 0;

    // Trending Content
    const topPost = trendingPosts?.sort((a: any, b: any) => {
      const scoreA = (a.wastein_post_curtidas?.[0]?.count || 0) + (a.wastein_post_comentarios?.[0]?.count || 0) * 2;
      const scoreB = (b.wastein_post_curtidas?.[0]?.count || 0) + (b.wastein_post_comentarios?.[0]?.count || 0) * 2;
      return scoreB - scoreA;
    })[0];

    return {
      alerts: {
        pendingUsers: pendingUsers || 0,
        pendingEcopontos: pendingEcopontos || 0,
        pendingExchanges: pendingExchanges || 0,
        pendingReports: pendingReports || 0,
        pendingLojistas: (pendingLojistasStores || 0) + (pendingLojistasRewards || 0)
      },
      discards: {
        totalVolume: totalVolume.toFixed(3),
        count: discards?.length || 0,
        topMaterial,
        volumeTrend: { val: `${Math.abs(volumeTrend)}%`, pos: volumeTrend >= 0 },
        countTrend: { val: `${Math.abs(countTrend)}%`, pos: countTrend >= 0 },
        todayCount: todayDiscards?.length || 0,
        weekCount: weekDiscards?.length || 0,
        monthCount: monthDiscards?.length || 0
      },
      ecopontos: {
        active: ecopontos?.filter(e => e.status_ativo).length || 0,
        zeroDiscardCount,
        topEcopontoName,
        topEcopontoVolume
      },
      machines: {
        total: totalMachines || 0,
        avgWeight: machineAvgWeight,
        topMachineName
      },
      partners: {
        total: totalPartners || 0,
        activeProducts: activeProducts || 0,
        topExchange
      },
      users: {
        total: totalUsers || 0,
        active: activeUsers || 0,
        checkinsToday: checkinsToday || 0,
        online: usersOnline || 0,
        conversionRate: totalUsers > 0 ? Math.round((completeUsers || 0) / totalUsers * 100) : 0,
        trend: { val: `${Math.abs(userTrend)}%`, pos: userTrend >= 0 }
      },
      impact: {
        trend: { val: `${Math.abs(co2Trend)}%`, pos: co2Trend >= 0 },
        avgPerUser: avgCO2PerUser.toFixed(1)
      },
      social: {
        posts: totalPosts || 0,
        likes: totalLikes || 0,
        comments: totalComments || 0,
        trending: topPost?.conteudo || 'Nenhum conteúdo em destaque'
      }
    };
    }, 30000);
  },

  // App Params & FAQ
  async getAppParams() {
    return this.getCached('app_params', async () => {
      const { data, error } = await supabase
        .from('app_params')
        .select('*')
        .single();
      
      if (error) throw error;
      return data;
    });
  },

  async getAppFaqs() {
    return this.getCached('app_faqs', async () => {
      const { data, error } = await supabase
        .from('app_faq')
        .select('*')
        .order('ordem', { ascending: true });
      
      if (error) throw error;
      return data;
    });
  },

  async getMerchantOrders(lojaId: string) {
    const { data, error } = await supabase
      .from('resgates')
      .select('*, recompensas(nome, imagem), usuarios(nome, email)')
      .eq('loja_id', lojaId)
      .eq('status', 'ANALISE')
      .order('data_registro', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getUserRedemptions(userId: string) {
    const { data, error } = await supabase
      .from('resgates')
      .select('*, recompensas(nome, imagem, valor), lojas(loja_nome)')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });
    if (error) throw error;
    return data;
  },

  async requestRedemption(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('requestRedemption', args);
  },

  async updateRedemptionStatus(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('updateRedemptionStatus', args);
  },

  async confirmRedemptionRelease(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('confirmRedemptionRelease', args);
  },

  async getMerchantProducts(lojaId: string) {
    const { data, error } = await supabase
      .from('recompensas')
      .select('*')
      .eq('loja_id', lojaId)
      .in('status', ['APROVADO', 'ANALISE'])
      .order('data_registro', { ascending: false });

    if (error) throw error;
    
    // Process signed URLs for images
    const processed = await Promise.all((data || []).map(async (r: any) => {
      const imagePath = r.imagem || r.imagem_url;
      if (imagePath) {
        if (!imagePath.startsWith('http')) {
          try {
            const signedUrl = await this.getSignedUrl(imagePath);
            r.imagem = signedUrl;
            r.imagem_url = signedUrl;
          } catch (e) {
            console.error("Error getting signed URL for merchant product image:", imagePath, e);
          }
        } else {
          r.imagem = imagePath;
          r.imagem_url = imagePath;
        }
      }
      return r;
    }));

    return processed;
  },

  async createReward(rewardData: any) {
    const { data, error } = await supabase
      .from('recompensas')
      .insert([{
        loja_id: rewardData.loja_id,
        parceiro_id: rewardData.parceiro_id,
        nome: rewardData.nome,
        descricao: rewardData.descricao,
        valor: rewardData.valor,
        quantidade: rewardData.quantidade,
        imagem: rewardData.imagem,
        status: 'ANALISE',
        status_ativo: false,
        data_registro: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getLojaColaboradores(lojaId: string) {
    const { data, error } = await supabase
      .from('loja_colaboradores')
      .select('*, usuarios(id, nome, email, photo_url)')
      .eq('loja_id', lojaId);

    if (error) throw error;
    return data;
  },

  async addLojaColaborador(lojaId: string, usuarioEmail: string) {
    const { data: userData, error: userError } = await supabase
      .from('usuarios')
      .select('id')
      .eq('email', usuarioEmail)
      .maybeSingle();

    if (userError) throw userError;
    if (!userData) throw new Error("Usuário não encontrado com este e-mail.");

    const { data, error } = await supabase
      .from('loja_colaboradores')
      .insert([{
        loja_id: lojaId,
        usuario_id: userData.id,
        data_vinculo: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateLoja(lojaId: string, lojaData: any) {
    const { data, error } = await supabase
      .from('lojas')
      .update({
        ...lojaData,
        data_atualizacao: new Date().toISOString()
      })
      .eq('id', lojaId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createNotification(notification: {
    usuario_id: string;
    titulo: string;
    mensagem: string;
    tipo: string;
    link?: string;
    metadata?: any;
  }) {
    const { data, error } = await supabase
      .from('notificacoes')
      .insert([{
        ...notification,
        lida: false,
        data_registro: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getMerchantOrderHistory(lojaId: string) {
    return this.getCached(`merchant_history_${lojaId}`, async () => {
      const { data, error } = await supabase
        .from('resgates')
        .select('*, recompensas(nome, imagem), usuarios(nome, email)')
        .eq('loja_id', lojaId)
        .neq('status', 'ANALISE')
        .order('data_registro', { ascending: false });

      if (error) throw error;
      return data;
    }, 60000); // 1 minute cache
  },

  async getPendingOrdersCount(lojaId: string) {
    return this.getCached(`pending_orders_${lojaId}`, async () => {
      const { count, error } = await supabase
        .from('resgates')
        .select('*', { count: 'exact', head: true })
        .eq('loja_id', lojaId)
        .eq('status', 'ANALISE');

      if (error) throw error;
      return count || 0;
    }, 30000); // 30 seconds cache
  },

  async getTotalOrdersCount(lojaId: string) {
    return this.getCached(`total_orders_${lojaId}`, async () => {
      const { count, error } = await supabase
        .from('resgates')
        .select('*', { count: 'exact', head: true })
        .eq('loja_id', lojaId);

      if (error) throw error;
      return count || 0;
    }, 60000); // 1 minute cache
  },

  async getParametrosGerais() {
    return this.getCached('parametros_gerais_cache', async () => {
      try {
        const { data, error } = await supabase
          .from('parametros_gerais')
          .select('*')
          .order('data_atualizacao', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (error) {
          console.warn("Could not fetch parametros_gerais with order, trying simple select:", error);
          const { data: simpleData } = await supabase.from('parametros_gerais').select('*').limit(1).maybeSingle();
          return simpleData;
        }
        return data;
      } catch (e) {
        console.error("Error fetching general parameters:", e);
        return null;
      }
    }, 60000);
  },

  async logError(error: any, context: string, userIdOrMetadata?: string | any, customMessage?: string) {
    let userId: string | null = null;
    let finalContext = context;
    
    if (typeof userIdOrMetadata === 'string') {
      userId = userIdOrMetadata;
    } else if (userIdOrMetadata && typeof userIdOrMetadata === 'object') {
      finalContext = `${context} | Metadata: ${JSON.stringify(userIdOrMetadata)}`;
      if (userIdOrMetadata.userId) userId = userIdOrMetadata.userId;
    }

    console.error(`[Error Log] Context: ${finalContext}`, error);
    
    // Show friendly toast to user
    const message = customMessage || 'Ocorreu um erro inesperado. Nossa equipe já foi notificada e estamos trabalhando para resolver.';
    toast.error(message, {
      description: `Contexto: ${context}`,
      duration: 5000,
    });

    try {
      const { error: logErr } = await supabase
        .from('logs_api')
        .insert([{
          contexto: finalContext,
          erro: typeof error === 'object' ? JSON.stringify(error) : String(error),
          usuario_id: userId || null,
          data_registro: new Date().toISOString()
        }]);
      
      if (logErr) {
        console.warn('Failed to insert error log into database:', logErr);
      }
    } catch (e) {
      console.warn('Error while attempting to log error to database:', e);
    }
  },

  async getMaterialByBarcode(barcode: string) {
    return this.getCached(`material_barcode_${barcode}`, async () => {
      const { data, error } = await supabase
        .from('materiais')
        .select('*')
        .eq('codigo_barras', barcode)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }, 300000); // 5 minutes cache
  },

  async getMedals() {
    return this.getCached('medals', async () => {
      const { data, error } = await supabase
        .from('medalhas')
        .select('*')
        .eq('ativa', true)
        .order('data_registro', { ascending: true });
      
      if (error) throw error;

      const shouldRefresh = (url: string) => {
        if (!url) return false;
        return !url.startsWith('http') || url.includes('/storage/v1/object/sign/');
      };

      // Process URLs
      const processed = await Promise.all((data || []).map(async (m: any) => {
        if (m.imagem_medalha && shouldRefresh(m.imagem_medalha)) {
          try {
            m.imagem_medalha = await this.getSignedUrl(m.imagem_medalha);
          } catch (e) {
            console.error("Error getting signed URL for medal image:", m.imagem_medalha, e);
          }
        }
        return m;
      }));

      return processed;
    });
  },

  async getUserMedals(userId: string) {
    return this.getCached(`user_medals_${userId}`, async () => {
      const { data, error } = await supabase
        .from('medalhas_user')
        .select('medalha_id, conquistado_em, imagem_medalha')
        .eq('usuario_id', userId);
      
      if (error) throw error;

      const shouldRefresh = (url: string) => {
        if (!url) return false;
        return !url.startsWith('http') || url.includes('/storage/v1/object/sign/');
      };

      const processed = await Promise.all((data || []).map(async (um: any) => {
        if (um.imagem_medalha && shouldRefresh(um.imagem_medalha)) {
          try {
            um.imagem_medalha = await this.getSignedUrl(um.imagem_medalha);
          } catch (e) {
            console.error("Error getting signed URL for user medal image:", um.imagem_medalha, e);
          }
        }
        return um;
      }));

      return processed;
    }, 60000); // 1 minute cache
  },

  async getAchievements() {
    return this.getCached('achievements', async () => {
      const { data, error } = await supabase
        .from('conquistas')
        .select('*')
        .eq('ativa', true)
        .order('valor_marco', { ascending: true });
      
      if (error) throw error;

      const shouldRefresh = (url: string) => {
        if (!url) return false;
        return !url.startsWith('http') || url.includes('/storage/v1/object/sign/');
      };

      // Process URLs
      const processed = await Promise.all((data || []).map(async (a: any) => {
        if (a.imagem_url && shouldRefresh(a.imagem_url)) {
          try {
            a.imagem_url = await this.getSignedUrl(a.imagem_url);
          } catch (e) {
            console.error("Error getting signed URL for achievement image:", a.imagem_url, e);
          }
        }
        return a;
      }));
      
      return processed;
    });
  },

  async getUserAchievements(userId: string) {
    return this.getCached(`user_achievements_${userId}`, async () => {
      const { data, error } = await supabase
        .from('conquistas_user')
        .select('conquista_id, conquistado_em')
        .eq('usuario_id', userId);
      
      if (error) throw error;
      return data;
    }, 60000); // 1 minute cache
  },

  async sendDiscardFeedback(feedback: {
    descarte_id: string;
    usuario_id: string;
    conteudo_id?: string;
    titulo: string;
    descricao: string;
    imagem_url?: string;
    evento?: string;
  }) {
    const { data, error } = await supabase
      .from('descartes_feedback')
      .insert([feedback])
      .select()
      .single();
    
    if (error) throw error;

    // Trigger notification
    try {
      await this.createNotification({
        usuario_id: feedback.usuario_id,
        titulo: 'Novo Feedback de Descarte',
        mensagem: `Você recebeu um novo feedback sobre seu descarte: ${feedback.titulo}. Acesse o WasteEduca para visualizar.`,
        tipo: 'FEEDBACK',
        lida: false
      });
    } catch (notifyErr) {
      console.error("Error creating feedback notification:", notifyErr);
    }

    return data;
  },

  async getUserFeedbacks(userId: string) {
    const { data, error } = await supabase
      .from('descartes_feedback')
      .select('*, descartes(data_registro)')
      .eq('usuario_id', userId)
      .order('data_registro', { ascending: false });
    
    if (error) throw error;

    // Process image URLs
    const processed = await Promise.all(data.map(async (f: any) => {
      if (f.imagem_url && !f.imagem_url.startsWith('http')) {
        try {
          f.imagem_url = await this.getSignedUrl(f.imagem_url);
        } catch (e) {
          console.error("Error getting signed URL for feedback image:", f.imagem_url, e);
        }
      }
      return f;
    }));

    return processed;
  },

  async markFeedbackAsViewed(feedbackId: string) {
    const { error } = await supabase
      .from('descartes_feedback')
      .update({ 
        visualizada: true,
        data_visualizacao: new Date().toISOString()
      })
      .eq('id', feedbackId);
    
    if (error) throw error;
  },

  async getDiscardFeedback(descarteId: string) {
    const { data, error } = await supabase
      .from('descartes_feedback')
      .select(`
        *,
        conteudos_educativos(titulo)
      `)
      .eq('descarte_id', descarteId);
    
    if (error) throw error;
    return data;
  },

  async getEducationalContents() {
    return this.getCached('educational_contents', async () => {
      const { data, error } = await supabase
        .from('conteudos_educacionais')
        .select('*')
        .order('titulo', { ascending: true });
      
      if (error) throw error;
      return data;
    }, 300000); // 5 minutes cache
  },

  async getEstimatedReach(audience: string, originFilter?: string, channel?: 'email' | 'sms') {
    let query = supabase.from('usuarios').select('id', { count: 'exact', head: true });

    if (audience !== 'Todos') {
      query = query.eq('tipo_perfil', (audience === 'Cidadãos' || audience === 'Usuários') ? 'Usuário' : audience === 'Catadores' ? 'Catador' : 'Empresa');
    }

    if (channel === 'email') {
      query = query.not('email', 'is', null).ilike('email', '%@%');
    } else if (channel === 'sms') {
      query = query.not('celular', 'is', null);
    }

    if (originFilter) {
      // Users who have discarded at this origin
      const { data: userIds } = await supabase
        .from('descartes')
        .select('usuario_id')
        .eq('origem', originFilter);
      
      if (userIds && userIds.length > 0) {
        const uniqueIds = Array.from(new Set(userIds.map(u => u.usuario_id)));
        query = query.in('id', uniqueIds);
      } else {
        return 0;
      }
    }

    const { count, error } = await query;
    if (error) throw error;
    return count || 0;
  },

  async getTargetUsers(audience: string, originFilter?: string) {
    let query = supabase.from('usuarios').select('id');

    if (audience !== 'Todos') {
      query = query.eq('tipo_perfil', (audience === 'Cidadãos' || audience === 'Usuários') ? 'Usuário' : audience === 'Catadores' ? 'Catador' : 'Empresa');
    }

    if (originFilter) {
      const { data: userIds } = await supabase
        .from('descartes')
        .select('usuario_id')
        .eq('origem', originFilter);
      
      if (userIds && userIds.length > 0) {
        const uniqueIds = Array.from(new Set(userIds.map(u => u.usuario_id)));
        query = query.in('id', uniqueIds);
      } else {
        return [];
      }
    }

    const { data, error } = await query;
    if (error) throw error;
    return data.map(u => u.id);
  },

  async getAvailableQuizzes(userId: string) {
    // 1. Get ecopontos where user has discarded
    const { data: discards, error: discardsError } = await supabase
      .from('descartes')
      .select('ecoponto_id')
      .eq('usuario_id', userId);
    
    if (discardsError) throw discardsError;
    if (!discards || discards.length === 0) return [];

    const ecopontoIds = Array.from(new Set(discards.map(d => d.ecoponto_id).filter(Boolean)));

    // 2. Get quizzes for these ecopontos
    const { data: quizLinks, error: linksError } = await supabase
      .from('quiz_ecopontos')
      .select('quiz_id, ecoponto_id, ecopontos(nome)')
      .in('ecoponto_id', ecopontoIds);
    
    if (linksError) throw linksError;
    if (!quizLinks || quizLinks.length === 0) return [];

    const quizIds = Array.from(new Set(quizLinks.map(l => l.quiz_id)));

    const { data: quizzes, error: quizzesError } = await supabase
      .from('quiz')
      .select('*')
      .in('id', quizIds)
      .eq('status', 'ATIVO');
    
    if (quizzesError) throw quizzesError;

    // 3. Check which ones user already answered
    const { data: answers, error: answersError } = await supabase
      .from('quiz_user')
      .select('quiz_id')
      .eq('user_id', userId);
    
    if (answersError) throw answersError;

    const answeredIds = new Set(answers?.map(a => a.quiz_id) || []);

    return quizzes.map(q => {
      const link = quizLinks.find(l => l.quiz_id === q.id);
      return {
        ...q,
        ecoponto_id: link?.ecoponto_id,
        ecopontos: link?.ecopontos,
        answered: answeredIds.has(q.id)
      };
    });
  },

  async getQuizDetails(quizId: string) {
    const { data: quiz, error: quizError } = await supabase
      .from('quiz')
      .select('*')
      .eq('id', quizId)
      .single();
    
    if (quizError) throw quizError;

    const { data: questions, error: questionsError } = await supabase
      .from('quiz_perguntas')
      .select('*')
      .eq('quiz_id', quizId)
      .order('ordem', { ascending: true });
    
    if (questionsError) throw questionsError;

    // Get alternatives for each question
    const questionsWithAlternatives = await Promise.all(questions.map(async (q: any) => {
      const { data: alternatives, error: altError } = await supabase
        .from('quiz_respostas')
        .select('*')
        .eq('pergunta_id', q.id)
        .order('ordem', { ascending: true });
      
      if (altError) throw altError;
      
      return {
        ...q,
        opcoes: alternatives.map(a => a.resposta),
        alternatives: alternatives, // Keep full data for checking correctness
        opcao_correta: alternatives.findIndex(a => a.verdadeira)
      };
    }));

    return { ...quiz, questions: questionsWithAlternatives };
  },

  async saveQuizResponse(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('saveQuizResponse', args);
  },

  async getQuizResult(userId: string, quizId: string) {
    const { data, error } = await supabase
      .from('quiz_user')
      .select(`
        *,
        pergunta:quiz_perguntas(
          pergunta,
          imagem_url,
          alternativas:quiz_respostas(resposta, verdadeira)
        )
      `)
      .eq('user_id', userId)
      .eq('quiz_id', quizId);
    
    if (error) throw error;
    if (!data || data.length === 0) return null;

    const correctCount = data.filter(d => d.correta).length;

    // Process signed URLs for images
    const processedRespostas = await Promise.all(data.map(async (res: any) => {
      if (res.imagem_resposta && !res.imagem_resposta.startsWith('http')) {
        try {
          res.imagem_resposta = await this.getSignedUrl(res.imagem_resposta);
        } catch (e) {
          console.error("Error getting signed URL for quiz answer image:", res.id, e);
        }
      }
      if (res.pergunta?.imagem_url && !res.pergunta.imagem_url.startsWith('http')) {
        try {
          res.pergunta.imagem_url = await this.getSignedUrl(res.pergunta.imagem_url);
        } catch (e) {
          console.error("Error getting signed URL for quiz question image:", res.id, e);
        }
      }
      return res;
    }));

    return {
      pontuacao: correctCount,
      total_perguntas: data.length,
      data_registro: data[0].data_registro,
      respostas: processedRespostas
    };
  },

  async registerDiscard(...args: any[]): Promise<any> {
    // Executado no servidor (pontos não são gravados pelo navegador)
    return chamarAcaoServidor('registerDiscard', args);
  },

  async getMaterials() {
    return this.getCached('all_materials', async () => {
      const { data, error } = await supabase
        .from('materiais')
        .select('*')
        .order('material');
      
      if (error) throw error;
      return data;
    });
  },

  async getMaterialBalances(userId: string) {
    const { data, error } = await supabase
      .from('usuario_material_saldo')
      .select('*, materiais(*)')
      .eq('usuario_id', userId);
    
    if (error) throw error;
    return data;
  },

  async decrementMaterialBalancesByDiscard(userId: string, discardId: string) {
    try {
      // 1. Fetch materials linked to this discard
      const { data: discardMaterials, error: fetchError } = await supabase
        .from('descartes_materiais')
        .select('material_id, gramas')
        .eq('descarte_id', discardId);

      if (fetchError) throw fetchError;
      if (!discardMaterials || discardMaterials.length === 0) return;

      // 2. Iterate and decrement each material balance
      for (const mat of discardMaterials) {
        if (!mat.material_id) continue;
        
        const weightInKg = (Number(mat.gramas) || 0) / 1000;
        
        const { data: currentBalance } = await supabase
          .from('usuario_material_saldo')
          .select('kg_saldo, id')
          .eq('usuario_id', userId)
          .eq('material_id', mat.material_id)
          .maybeSingle();

        if (currentBalance) {
          let newKgSaldo = Number(currentBalance.kg_saldo) - weightInKg;
          
          /**
           * To maintain consistency with the accumulation logic:
           * If we go negative, we wrap around.
           * 1kg = 1 points/unit of awarded points.
           * Since points were already decremented in the main flow, 
           * we just adjust the fractional residue.
           */
          while (newKgSaldo < 0) {
            newKgSaldo += 1.0;
          }

          await supabase
            .from('usuario_material_saldo')
            .update({ 
              kg_saldo: Number(newKgSaldo.toFixed(4)),
              data_atualizacao: new Date().toISOString()
            })
            .eq('usuario_id', userId)
            .eq('material_id', mat.material_id);
        }
      }
    } catch (err) {
      console.error('Error in decrementMaterialBalancesByDiscard:', err);
      // We don't throw here to not block the main deletion flow, 
      // but logging it is important.
    }
  },

  async getUserMaterialSaldo(userId: string) {
    const [allMaterials, userBalances] = await Promise.all([
      this.getMaterials(),
      this.getMaterialBalances(userId)
    ]);

    return allMaterials.map(m => {
      const balance = userBalances?.find((b: any) => b.material_id === m.id);
      return {
        material_id: m.id,
        kg_saldo: balance ? balance.kg_saldo : 0,
        materiais: m
      };
    });
  },

  async trackMissionEvent(userId: string, eventType: any, metadata: any) {
    return gamificationService.processarEventoUsuario(userId, eventType, metadata);
  },

  async awardXPForContent(userId: string, contentId: string) {
    // To prevent spamming, we could use a specific table or cache
    // But for the requirement "assisting educational content", we'll just award the value
    try {
      const config = await this.getExpConfig('conteudo_educacional');
      const expValue = config ? config.valor : 20;
      
      // We can check if user already viewed this content recently to avoid exploit
      // For now, let's just award it.
      await this.updateUserXP(userId, expValue);
    } catch (e) {
      console.error("Error awarding content XP:", e);
    }
  },

  // Scanner Products Management
  async getScannerProducts() {
    const { data, error } = await supabase
      .from('produtos_barcode')
      .select('*');
    
    if (error) throw error;
    if (!data) return [];

    const sanitized = data.map((item: any) => {
      let cleanBarcode = item.barcode;
      if (item.barcode && typeof item.barcode === 'string') {
        cleanBarcode = item.barcode.replace(/^["'\s]+|["'\s]+$/g, '').trim();
      }
      return {
        ...item,
        barcode: cleanBarcode,
        fonte: normalizeProductSource(item.fonte),
        confianca: item.confianca !== undefined && item.confianca !== null ? normalizeConfidence(item.confianca) : item.confianca
      };
    });

    return sanitized.sort((a: any, b: any) => {
      const dateA = new Date(a.data_registro || a.created_at || 0).getTime();
      const dateB = new Date(b.data_registro || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  },

  async updateScannerProduct(idOrBarcode: string, updates: any) {
    const payload = {
      ...updates,
      data_atualizacao: new Date().toISOString()
    };
    if (payload.confianca !== undefined && payload.confianca !== null) {
      payload.confianca = normalizeConfidence(payload.confianca);
    }
    if (payload.fonte !== undefined && payload.fonte !== null) {
      payload.fonte = normalizeProductSource(payload.fonte);
    }
    let { data, error } = await supabase
      .from('produtos_barcode')
      .update(payload)
      .eq('id', idOrBarcode)
      .select();
    
    if (error || !data || data.length === 0) {
      const res = await supabase
        .from('produtos_barcode')
        .update(payload)
        .eq('barcode', idOrBarcode)
        .select();
      data = res.data;
      error = res.error;
    }
    
    if (error) throw error;
    return data?.[0] || null;
  },

  async deleteScannerProduct(idOrBarcode: string) {
    let { error } = await supabase
      .from('produtos_barcode')
      .delete()
      .eq('id', idOrBarcode);
    
    if (error) {
      const res = await supabase
        .from('produtos_barcode')
        .delete()
        .eq('barcode', idOrBarcode);
      error = res.error;
    }
    
    if (error) throw error;
  },

  async dispatchPushNotifications(dispatchData: {
    title: string;
    message: string;
    audience: string;
    originFilter: string;
    link?: string;
  }) {
    // 1. Log the dispatch in push_notifications
    const { data: dispatchRecord, error: dispatchError } = await supabase
      .from('push_notifications')
      .insert([{
        titulo: dispatchData.title,
        mensagem: dispatchData.message,
        origem: dispatchData.originFilter || 'Manager',
        alcance: 0, // Will update after fetch
        publico_alvo: dispatchData.audience || 'Todos',
        status: 'enviado'
      }])
      .select()
      .single();

    if (dispatchError) throw dispatchError;

    // 2. Fetch users based on audience
    let usersQuery = supabase.from('usuarios').select('id');

    if (dispatchData.audience && dispatchData.audience !== 'Todos') {
      // Map audience labels to profile types if needed, but assuming they match (Usuários, Catadores, Empresas)
      const mappedAudience = dispatchData.audience === 'Usuários' ? 'Usuário' : 
                            dispatchData.audience === 'Catadores' ? 'Catador' : 
                            dispatchData.audience === 'Empresas' ? 'Empresa' : dispatchData.audience;
      
      usersQuery = usersQuery.eq('tipo_perfil', mappedAudience);
    }

    const { data: users, error: usersError } = await usersQuery;
    if (usersError) throw usersError;

    if (users && users.length > 0) {
      // Update reach in the log
      await supabase
        .from('push_notifications')
        .update({ alcance: users.length })
        .eq('id', dispatchRecord.id);

      // 3. Insert into notificacoes table for each user
      const notifications = users.map(user => ({
        usuario_id: user.id,
        titulo: dispatchData.title,
        mensagem: dispatchData.message,
        tipo: 'PUSH',
        lida: false,
        link: dispatchData.link || null,
        metadata: { dispatch_id: dispatchRecord.id }
      }));

      // Insert in batches
      for (let i = 0; i < notifications.length; i += 500) {
        const batch = notifications.slice(i, i + 500);
        await supabase.from('notificacoes').insert(batch);
      }
    }

    return dispatchRecord;
  },

  async getPartnerHomologatedEcopontos(parceiroId: string) {
    const { data, error } = await supabase
      .from('ecopontos_parceiros_homol')
      .select('ecoponto_id, ecopontos(*)')
      .eq('parceiro_id', parceiroId);
    
    if (error) throw error;
    
    return (data || []).map(h => {
      const e = h.ecopontos;
      return Array.isArray(e) ? e[0] : e;
    }).filter(Boolean);
  }
};
